import { Backdrop, Box, Button, Chip, CircularProgress, Typography, useTheme, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, useMediaQuery, Tabs, Tab, ToggleButton, ToggleButtonGroup, TextField, InputAdornment, Select, MenuItem, FormControl, IconButton } from "@mui/material";
import { WhatsApp, Telegram, Email, Sms, Edit, Delete, Search, Refresh, ChevronLeft, ChevronRight, AutoAwesome, LocalOffer } from "@mui/icons-material";
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import TableRowsIcon from '@mui/icons-material/TableRows';
import MenuIcon from '@mui/icons-material/Menu';
import { LockKeyhole } from 'lucide-react';


import { DataSets } from "modules/Interfaces/InstanUpdates";
import { Template } from "modules/Interfaces/InstanUpdates/Template";
import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { useSelector } from "react-redux";
import { RootState } from "state/store";
import { Logger } from "utils/Logger";
import { trackAction } from "utils/Infralytiqs";
import { apiClient } from "utils/ApiClient";
import { API_BASE_URL } from "envConfig";

import { TemplateDialog } from "./TemplateDialog";
import { DataTableWithStaticSchema } from "components/DataTable"; // Import the new component
import { DynamicBreadcrumbs } from "components/Breadcrumb";

import { useModulePermissions } from 'hooks/useModulePermissions';
import {
    SWIPE_ACTION_WIDTH_PX,
    SWIPE_DIRECTION_LOCK_PX,
    clampSwipeOffset,
    computeSnappedOffset,
} from 'utils/iosSwipeRow';
import { RegisterEmailTemplate } from "components/TemplateEditor/EmailTemplate/RegisterEmailTemplate";
import { EmailTemplateDialog } from "components/TemplateEditor/EmailTemplate/EmailTemplateDialog";
import { generateThumbnailFromHtml, generateThumbnailFromText } from "utils/ThumbnailGenerator";
import { hasInstantUpdateBusinessPlusOrEnterprise, normalizeLicenseType } from "utils/productPlan";

export const Templates: React.FC = () => {
    const [templates, setTemplates] = useState<Template[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const { canManageTemplates, canViewOnly, isUserSuperAdmin } = useModulePermissions();
    const [isEditing, setIsEditing] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [currentTemplate, setCurrentTemplate] = useState<Partial<Template> | null>(null);
    const [dataSets, setDataSets] = useState<DataSets[]>([]);
    const [createTemplateDialogOpen, setCreateTemplateDialogOpen] = useState(false);

    const TEMPLATES_ACTIVE_TAB_STORAGE_KEY = 'instant-updates-templates-active-tab';
    const [activeTab, setActiveTab] = useState<string>(() => {
        try {
            const stored = localStorage.getItem(TEMPLATES_ACTIVE_TAB_STORAGE_KEY);
            if (
                stored === 'whatsapp' ||
                stored === 'mail' ||
                stored === 'sms' ||
                stored === 'telegram' ||
                stored === 'whatsappbusiness'
            ) {
                return stored;
            }
        } catch {
            /* ignore quota / private mode */
        }
        return 'whatsapp';
    });
    const [registerEmailDialogOpen, setRegisterEmailDialogOpen] = useState(false);
    const [emailEditDialogOpen, setEmailEditDialogOpen] = useState(false);
    const [editingEmailTemplate, setEditingEmailTemplate] = useState<Template | null>(null);

    // Delete confirmation dialog state
    const [deleteConfirmDialogOpen, setDeleteConfirmDialogOpen] = useState(false);
    const [templateToDelete, setTemplateToDelete] = useState<any>(null);
    const [deleteLoading, setDeleteLoading] = useState(false);

    const [templateNameError, setTemplateNameError] = useState('');
    const [saveError, setSaveError] = useState<string>('');
    const [templateDataRefreshKey, setTemplateDataRefreshKey] = useState(0);
    const [refreshingMetaStatus, setRefreshingMetaStatus] = useState(false);
    const [availableColumns, setAvailableColumns] = useState<string[]>([]);
    const [loadingColumns, setLoadingColumns] = useState<boolean>(false);
    const [lastFetchedDataSet, setLastFetchedDataSet] = useState<string>('');
    const theme = useTheme();
    const isProfessionalLightDialog = Boolean(
        theme.palette.mode === 'light'
        && (
            theme.palette.dashboard?.welcomePanelGradient
            || theme.palette.dashboard?.counterHeroGradient
            || theme.palette.dashboard?.heroTileForegroundColor
        ),
    );
    const dialogTitleCloseButtonSx = {
        minWidth: 'auto' as const,
        px: 1.5,
        fontSize: '0.875rem',
        fontWeight: 700,
        ...(isProfessionalLightDialog
            ? {
                color: '#ffffff !important',
                borderColor: 'rgba(255, 255, 255, 0.7) !important',
                '&.MuiButton-outlinedSecondary': {
                    color: '#ffffff !important',
                    borderColor: 'rgba(255, 255, 255, 0.7) !important',
                },
                '&:hover': {
                    borderColor: '#ffffff !important',
                    backgroundColor: 'rgba(255, 255, 255, 0.12) !important',
                    color: '#ffffff !important',
                },
            }
            : {}),
    };
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    /** Mobile/tablet: iOS-style swipe on template tiles (Edit / Delete) */
    const [templateTileSwipeRow, setTemplateTileSwipeRow] = useState<{
        tileId: string;
        offset: number;
        dragging: boolean;
    } | null>(null);
    const templateSwipeSessionRef = useRef<{
        tileId: string;
        startX: number;
        startY: number;
        baseOffset: number;
        lock: 'h' | 'v' | null;
    } | null>(null);
    const templateLatestSwipeOffsetRef = useRef(0);

    const VIEWMODE_STORAGE_KEY = 'content-templates-view-mode';
    const [viewMode, setViewMode] = useState<'tile' | 'table'>(() => {
        const stored = localStorage.getItem(VIEWMODE_STORAGE_KEY);
        return stored === 'table' ? 'table' : 'tile';
    });
    const [tileTemplates, setTileTemplates] = useState<any[]>([]);
    const [tileLoading, setTileLoading] = useState(false);
    const [tileSearchQuery, setTileSearchQuery] = useState('');
    const [tableSearchQuery, setTableSearchQuery] = useState('');
    const [tileSortField, setTileSortField] = useState<'name' | 'lastUpdated'>('lastUpdated');
    const [tileSortOrder, setTileSortOrder] = useState<'asc' | 'desc'>('desc');
    const TILE_PAGE_SIZE = 20;
    const [tileVisibleCount, setTileVisibleCount] = useState(TILE_PAGE_SIZE);
    const [thumbnailPreview, setThumbnailPreview] = useState<string | null>(null);
    const [toolbarMenuOpen, setToolbarMenuOpen] = useState(false);
    const [categoryFilter, setCategoryFilter] = useState<string>('');

    const [systemTemplates, setSystemTemplates] = useState<any[]>([]);
    const [systemTemplatesLoading, setSystemTemplatesLoading] = useState(false);
    const [systemTemplatePreview, setSystemTemplatePreview] = useState<any>(null);
    const [browseSystemTemplatesOpen, setBrowseSystemTemplatesOpen] = useState(false);
    const systemTemplatesScrollRef = useRef<HTMLDivElement>(null);
    const [sysSearchQuery, setSysSearchQuery] = useState('');
    const [sysCategoryFilter, setSysCategoryFilter] = useState('');
    const [sysSortField, setSysSortField] = useState<'name' | 'lastUpdated'>('lastUpdated');
    const [sysSortOrder, setSysSortOrder] = useState<'asc' | 'desc'>('desc');
    const SYS_PAGE_SIZE = 20;
    const [sysVisibleCount, setSysVisibleCount] = useState(SYS_PAGE_SIZE);
    const [prefillEmailContent, setPrefillEmailContent] = useState<{ content: string; subject: string; builderConfig?: any } | null>(null);
    const [preselectedSystemTemplateId, setPreselectedSystemTemplateId] = useState<string | null>(null);

    const user = useSelector((state: RootState) => state.authentication.user);
    const company = useSelector((state: RootState) => state.authentication.company);

    const TABS: { label: string; value: string; icon: React.ReactElement; gradient: string; hoverGradient: string; premium?: boolean }[] = [
        { label: 'WhatsApp', value: 'whatsapp', icon: <WhatsApp fontSize="small" />, gradient: 'linear-gradient(135deg, #2e7d32 0%, #1b5e20 100%)', hoverGradient: 'linear-gradient(135deg, #388e3c 0%, #2e7d32 100%)' },
        { label: 'Email', value: 'mail', icon: <Email fontSize="small" />, gradient: 'linear-gradient(135deg, #ed6c02 0%, #e65100 100%)', hoverGradient: 'linear-gradient(135deg, #f57c00 0%, #ed6c02 100%)' },
        { label: 'SMS', value: 'sms', icon: <Sms fontSize="small" />, gradient: 'linear-gradient(135deg, #9c27b0 0%, #7b1fa2 100%)', hoverGradient: 'linear-gradient(135deg, #9c27b0 0%, #7b1fa2 100%)', premium: true },
        { label: 'Telegram', value: 'telegram', icon: <Telegram fontSize="small" />, gradient: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)', hoverGradient: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)', premium: true },
        { label: 'WhatsApp Business', value: 'whatsappbusiness', icon: <WhatsApp fontSize="small" />, gradient: 'linear-gradient(135deg, #25d366 0%, #128c7e 100%)', hoverGradient: 'linear-gradient(135deg, #25d366 0%, #128c7e 100%)', premium: true },
    ];

    const getTabConfig = useCallback((tabValue: string) => {
        return TABS.find(t => t.value === tabValue) || TABS[0];
    }, []);

    const formatDate = (dateStr: string) => {
        const date = new Date(dateStr);
        return date.toLocaleString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false });
    };

    const pendingFetches = useRef<Record<string, Promise<any>>>({});
    const deduplicatedGet = useCallback((endpoint: string) => {
        let promise = pendingFetches.current[endpoint];
        if (!promise) {
            promise = apiClient.get(endpoint);
            pendingFetches.current[endpoint] = promise;
            promise.finally(() => { delete pendingFetches.current[endpoint]; });
        }
        return promise;
    }, []);

    const fetchTileData = useCallback(async (tabValue: string, signal?: { cancelled: boolean }) => {
        try {
            setTileLoading(true);
            const data = await deduplicatedGet(`/iu/contenttemplate/company/${tabValue}`);
            if (signal?.cancelled) return;
            const templates = Array.isArray(data.data) ? data.data : [];
            setTileTemplates(templates);
        } catch (error) {
            if (signal?.cancelled) return;
            Logger.error("Failed to fetch content templates for tile view:", error);
            setTileTemplates([]);
        } finally {
            if (!signal?.cancelled) setTileLoading(false);
        }
        try {
            setSystemTemplatesLoading(true);
            const sysData = await deduplicatedGet(`/iu/systemtemplate/catalog/${tabValue}`);
            if (signal?.cancelled) return;
            setSystemTemplates(Array.isArray(sysData.data) ? sysData.data : []);
        } catch {
            if (signal?.cancelled) return;
            setSystemTemplates([]);
        } finally {
            if (!signal?.cancelled) setSystemTemplatesLoading(false);
        }
    }, [deduplicatedGet]);

    useEffect(() => {
        const signal = { cancelled: false };
        fetchTileData(activeTab, signal);
        return () => { signal.cancelled = true; };
    }, [activeTab, templateDataRefreshKey, fetchTileData]);

    const filteredTileTemplates = useMemo(() => {
        let results = tileTemplates.filter((tmpl: any) => (tmpl.category || '').toLowerCase() !== 'system');
        if (categoryFilter) {
            results = results.filter((tmpl: any) => (tmpl.category || '').toLowerCase() === categoryFilter.toLowerCase());
        }
        if (tileSearchQuery.trim()) {
            const q = tileSearchQuery.toLowerCase();
            results = results.filter((tmpl: any) => {
                const fullName = (tmpl.templateName || '').toLowerCase();
                const parts = fullName.split('-');
                const displayName = (parts.length >= 3 && parts[0] === 'iut') ? parts.slice(2).join('-') : fullName;
                const subject = (tmpl.emailSubject || '').toLowerCase();
                const keywordsMatch = Array.isArray(tmpl.keywords) && tmpl.keywords.some((kw: string) => (kw || '').toLowerCase().includes(q));
                return displayName.includes(q) || fullName.includes(q) || subject.includes(q) || keywordsMatch;
            });
        }
        const sorted = [...results].sort((a: any, b: any) => {
            let cmp = 0;
            if (tileSortField === 'name') {
                const nameA = (a.templateName || '').toLowerCase();
                const nameB = (b.templateName || '').toLowerCase();
                cmp = nameA.localeCompare(nameB);
            } else {
                const dateA = new Date(a.templateUpdatedOn || a.updatedAt || 0).getTime();
                const dateB = new Date(b.templateUpdatedOn || b.updatedAt || 0).getTime();
                cmp = dateA - dateB;
            }
            return tileSortOrder === 'asc' ? cmp : -cmp;
        });
        return sorted;
    }, [tileTemplates, tileSearchQuery, tileSortField, tileSortOrder, categoryFilter]);

    const filteredSystemTemplates = useMemo(() => {
        const sysDisplayName = (fullName: string) => {
            if (!fullName) return '';
            return fullName.toLowerCase().startsWith('iust-') ? fullName.substring(5) : fullName;
        };
        let results = systemTemplates.filter((tmpl: any) => (tmpl.category || '').toLowerCase() !== 'system');
        if (sysCategoryFilter) {
            results = results.filter((tmpl: any) => (tmpl.category || '').toLowerCase() === sysCategoryFilter.toLowerCase());
        }
        if (sysSearchQuery.trim()) {
            const q = sysSearchQuery.toLowerCase();
            results = results.filter((tmpl: any) => {
                const displayName = sysDisplayName(tmpl.templateName || '').toLowerCase();
                const fullName = (tmpl.templateName || '').toLowerCase();
                const keywordsMatch = Array.isArray(tmpl.keywords) && tmpl.keywords.some((kw: string) => (kw || '').toLowerCase().includes(q));
                return displayName.includes(q) || fullName.includes(q) || keywordsMatch;
            });
        }
        const sorted = [...results].sort((a: any, b: any) => {
            let cmp = 0;
            if (sysSortField === 'name') {
                cmp = (a.templateName || '').toLowerCase().localeCompare((b.templateName || '').toLowerCase());
            } else {
                cmp = new Date(a.updatedAt || 0).getTime() - new Date(b.updatedAt || 0).getTime();
            }
            return sysSortOrder === 'asc' ? cmp : -cmp;
        });
        return sorted;
    }, [systemTemplates, sysSearchQuery, sysCategoryFilter, sysSortField, sysSortOrder]);

    useEffect(() => {
        setSysVisibleCount(SYS_PAGE_SIZE);
    }, [sysSearchQuery, sysCategoryFilter, activeTab]);

    useEffect(() => {
        setTileVisibleCount(TILE_PAGE_SIZE);
    }, [tileSearchQuery, activeTab, categoryFilter]);

    const hasBusinessPlusOrEnterprise = useMemo(
        () => hasInstantUpdateBusinessPlusOrEnterprise(company),
        [company],
    );

    const visibleTabs = useMemo(
        () => TABS.filter((tab) => {
            if (tab.value === 'telegram' || tab.value === 'whatsappbusiness') {
                return hasBusinessPlusOrEnterprise;
            }
            return true;
        }),
        [hasBusinessPlusOrEnterprise],
    );

    // Determine if SMS is licensed for the company
    const hasSmsLicense = useMemo(() => {
        if (!company || !Array.isArray(company.productPlans)) return false;
        const allowedTypes = new Set(['BUSINESS', 'BUSINESS_PLUS', 'ENTERPRISE']);
        const plan = company.productPlans.find((plan: any) => plan.module === 'instantupdate.ai' && plan.status === 'active');
        const normalized = normalizeLicenseType(plan?.licenseType);
        return !!(plan && normalized && allowedTypes.has(normalized));
    }, [company, normalizeLicenseType]);

    // Normalized license helpers for other premium tabs
    const normalizedLicense = useMemo(() => {
        if (!company || !Array.isArray(company.productPlans)) return '';
        const plan = company.productPlans.find((plan: any) => plan.module === 'instantupdate.ai' && plan.status === 'active');
        return normalizeLicenseType(plan?.licenseType);
    }, [company, normalizeLicenseType]);

    const hasWabizLicense = useMemo(() => normalizedLicense === 'ENTERPRISE', [normalizedLicense]);
    const hasTelegramLicense = useMemo(() => normalizedLicense === 'BUSINESS_PLUS' || normalizedLicense === 'ENTERPRISE', [normalizedLicense]);

    const isTabLicensed = useCallback((tabValue: string) => {
        switch (tabValue) {
            case 'sms':
                return hasSmsLicense;
            case 'whatsappbusiness':
                return hasWabizLicense;
            case 'telegram':
                return hasTelegramLicense;
            default:
                return true;
        }
    }, [hasSmsLicense, hasWabizLicense, hasTelegramLicense]);

    /** Premium tabs without a matching license cannot create templates; hide AI Templates browse in those cases. */
    const canShowAiTemplatesForTabValue = useCallback((tabValue: string) => {
        const cfg = TABS.find(t => t.value === tabValue);
        if (!cfg?.premium) return true;
        return isTabLicensed(tabValue);
    }, [TABS, isTabLicensed]);

    useEffect(() => {
        if (visibleTabs.some((t) => t.value === activeTab)) return;
        const fallback = visibleTabs[0]?.value ?? 'whatsapp';
        setActiveTab(fallback);
        try {
            localStorage.setItem(TEMPLATES_ACTIVE_TAB_STORAGE_KEY, fallback);
        } catch {
            /* ignore quota / private mode */
        }
    }, [activeTab, visibleTabs]);

    useEffect(() => {
        if (!browseSystemTemplatesOpen) return;
        if (!canShowAiTemplatesForTabValue(activeTab)) {
            setBrowseSystemTemplatesOpen(false);
        }
    }, [activeTab, browseSystemTemplatesOpen, canShowAiTemplatesForTabValue]);

    const templateTypeOptions = [
        { key: "mail", label: "Mail" },
        { key: "sms", label: "SMS" },
        { key: "telegram", label: "TELEGRAM" },
        { key: "whatsapp", label: "WHATSAPP" },
    ];

    const templateSchema = useMemo(() => ({
        paths: {
            _id: { instance: 'String', isRequired: false },
            companyId: { instance: 'String', isRequired: false },
            templateName: { instance: 'String', isRequired: true },
            templateType: { instance: 'String', isRequired: true },
            estimatedCreditCost: { instance: 'Number', isRequired: false },
            templateContent: { instance: 'String', isRequired: true },
            emailSubject: { instance: 'String', isRequired: false },
            collectionId: { instance: 'String', isRequired: false },
            accessLevel: { instance: 'String', isRequired: true },
            sharedTo: { instance: 'Array', isRequired: false },
            templateCreatedByUserId: { instance: 'String', isRequired: false },
            templateUpdatedByUserId: { instance: 'String', isRequired: false },
            templateUpdatedOn: { instance: 'Date', isRequired: false },
            status: { instance: 'String', isRequired: false },
            createdAt: { instance: 'Date', isRequired: false },
            updatedAt: { instance: 'Date', isRequired: false },
            __v: { instance: 'Number', isRequired: false },
        },
        obj: {}
    }), []);

    // Memoize the deleteTemplate function to ensure stable reference
    const deleteTemplate = useCallback(async (template_id: string) => {
        try {
            setLoading(true);
            // Use ApiClient instead of fetch (automatically includes Authorization header)
            await apiClient.delete(`/iu/contenttemplate/${template_id}`);
            setTemplateDataRefreshKey(prev => prev + 1); // Trigger DataTable refresh
            setLoading(false);
        } catch (error) {
            setLoading(false);
            Logger.error("Failed to delete template:", error);
        }
    }, []); // Empty dependency array means this function is created once

    // Memoize the saveTemplate function to ensure stable reference
    const saveTemplate = useCallback(async () => {
        if (!currentTemplate) return;
        try {
            setSaveError('');
            setLoading(true);
            const { _id, ...payloadToSend } = currentTemplate;
            delete (payloadToSend as any).estimatedCreditCost;

            const templateType = (payloadToSend.templateType || '').toLowerCase();
            // Mail: force pending until moderator approves. SMS: server sets approval (e.g. auto-approve when body matches IU system template).
            if (templateType === 'mail') {
                (payloadToSend as any).isApproved = null;
            }
            if (templateType === 'sms') {
                delete (payloadToSend as any).isApproved;
                delete (payloadToSend as any).isApprovalRequired;
            }
            
            // Optimize media attachments for update operations
            if (isEditing && payloadToSend.mediaAttachments) {
                // For updates, send new media attachments and track deleted ones
                const newAttachments = payloadToSend.mediaAttachments.filter(
                    (attachment: any) => attachment.isNew === true
                );
                const deletedAttachments = payloadToSend.mediaAttachments.filter(
                    (attachment: any) => attachment.isDeleted === true
                );
                const orphanedAttachments = payloadToSend.mediaAttachments.filter(
                    (attachment: any) => attachment.isOrphaned === true
                );
                
                // For new attachments, include the data field since the media was uploaded separately
                // but we need to associate it with the template
                const optimizedNewAttachments = newAttachments.map((attachment: any) => ({
                    type: attachment.type,
                    filename: attachment.filename,
                    mimeType: attachment.mimeType,
                    size: attachment.size,
                    caption: attachment.caption,
                    data: attachment.data // Keep the data for new attachments
                }));
                
                payloadToSend.mediaAttachments = optimizedNewAttachments;
                payloadToSend.deletedMediaAttachments = [
                    ...deletedAttachments.map((att: any) => att.filename),
                    ...orphanedAttachments.map((att: any) => att.filename)
                ];
                

            }
            
            try {
                let thumbnailDataUrl: string | null = null;
                if (templateType === 'whatsapp' || templateType === 'sms' || templateType === 'telegram' || templateType === 'whatsappbusiness') {
                    thumbnailDataUrl = await generateThumbnailFromText(payloadToSend.templateContent || '', templateType);
                } else if (templateType === 'mail') {
                    thumbnailDataUrl = await generateThumbnailFromHtml(payloadToSend.templateContent || '');
                }
                if (thumbnailDataUrl) {
                    (payloadToSend as any).thumbnail = { data: thumbnailDataUrl, mimeType: 'image/png' };
                }
            } catch (thumbErr) {
                Logger.error("Thumbnail generation failed (non-blocking):", thumbErr);
            }

            if (isEditing) {
                await apiClient.put(`/iu/contenttemplate/${currentTemplate._id}`, payloadToSend);
            } else {
                await apiClient.post('/iu/contenttemplate/new', payloadToSend);
            }

            trackAction(
                isEditing ? 'template_updated' : 'template_created',
                (payloadToSend as any)?.templateType,
                {
                    template_id: currentTemplate._id,
                    template_name: (payloadToSend as any)?.templateName,
                    template_type: (payloadToSend as any)?.templateType,
                    template_category: (payloadToSend as any)?.templateCategory,
                    user_email: user?.email,
                },
            );

            setTemplateDataRefreshKey(prev => prev + 1);
            closeDialog();
            setLoading(false);
        } catch (error) {
            setLoading(false);
            Logger.error("Failed to save template:", error);
            setSaveError(error instanceof Error ? error.message : 'Failed to save template');
        }
    }, [currentTemplate, isEditing]);



    const fetchDataSets = async () => {
        try {
            setLoading(true);
            // Use ApiClient instead of fetch (automatically includes Authorization header)
            const data = await apiClient.get('/iu/ds/cmp/set');
            
            if (Array.isArray(data.data)) {
                setDataSets(data.data);
                setLoading(false);
                Logger.debug("src/modules/InstantUpdate/Templates/Templates.ts : fetchDataSets(): ", `DataSets: ${data.data.length} items`);
            } else {
                setDataSets([]);
                setLoading(false);
                Logger.debug("src/modules/InstantUpdate/Templates/Templates.ts : fetchDataSets(): Expected an array, received:", data.data);
            }

        } catch (error) {
            setDataSets([]);
            setLoading(false);
            Logger.debug("src/modules/InstantUpdate/Templates/Templates.ts : fetchDataSets(): ", "Failed to fetch DataSets:" + error);
        }
    };
    useEffect(() => {
        fetchDataSets();
    }, []);

    // Fetch columns when editingEmailTemplate changes
    useEffect(() => {
        if (editingEmailTemplate?.collectionId) {
            fetchDataSetColumns(editingEmailTemplate.collectionId);
        } else {
            setAvailableColumns([]);
        }
    }, [editingEmailTemplate?.collectionId, dataSets]);

    const openDialog = (template?: Template) => {
        setCurrentTemplate(
            template || {
                companyId: user.companyId,
                templateName: "",
                templateType: "mail",
                templateContent: "",
                collectionId: "",
                accessLevel: "public"
            }
        );
        setIsEditing(!!template);
        setDialogOpen(true);
        setTemplateNameError('');
        setSaveError('');
    };

    const openWhatsAppDialog = (template?: Template) => {
        setCurrentTemplate(
            template || {
                companyId: user.companyId,
                templateName: "",
                templateType: "whatsapp",
                templateContent: "",
                collectionId: "",
                accessLevel: "public"
            }
        );
        setIsEditing(!!template);
        setDialogOpen(true);
        setTemplateNameError('');
        setSaveError('');
    };

    const openTelegramDialog = (template?: Template) => {
        setCurrentTemplate(
            template || {
                companyId: user.companyId,
                templateName: "",
                templateType: "telegram",
                templateContent: "",
                collectionId: "",
                accessLevel: "public"
            }
        );
        setIsEditing(!!template);
        setDialogOpen(true);
        setTemplateNameError('');
        setSaveError('');
    };

    const openEmailDialog = (template?: Template) => {
        if (template) {
            // Edit existing template - open EmailTemplateDialog
            setEditingEmailTemplate(template);
            setEmailEditDialogOpen(true);
        } else {
            // Create new template - open RegisterEmailTemplate dialog
            setRegisterEmailDialogOpen(true);
        }
        setTemplateNameError('');
        setSaveError('');
    };

    const openSMSDialog = (template?: Template) => {
        setCurrentTemplate(
            template || {
                companyId: user.companyId,
                templateName: "",
                templateType: "sms",
                templateContent: "",
                collectionId: "",
                accessLevel: "public"
            }
        );
        setIsEditing(!!template);
        setDialogOpen(true);
        setTemplateNameError('');
        setSaveError('');
    };

    const openWhatsAppBusinessDialog = (template?: Template) => {
        setCurrentTemplate(
            template || {
                companyId: user.companyId,
                templateName: "",
                templateType: "whatsappbusiness",
                templateContent: "",
                collectionId: "",
                accessLevel: "public",
                whatsappBusinessConfig: {
                    language: 'en_US',
                    header: { type: 'none' },
                    footer: '',
                    buttons: [],
                },
            }
        );
        setIsEditing(!!template);
        setDialogOpen(true);
        setTemplateNameError('');
        setSaveError('');
    };

    const closeDialog = () => {
        setDialogOpen(false);
        setCurrentTemplate(null);
        setTemplateNameError('');
        setSaveError('');
    };

    // Function to fetch dataset schema and extract column names
    const fetchDataSetColumns = async (dataSetId: string) => {
        if (!dataSetId) {
            setAvailableColumns([]);
            setLastFetchedDataSet('');
            return;
        }

        // Prevent duplicate API calls for the same dataset
        if (lastFetchedDataSet === dataSetId) {
            return;
        }

        try {
            setLoadingColumns(true);
            const selectedDataSet = dataSets.find(ds => ds._id === dataSetId);
            if (!selectedDataSet) {
                setAvailableColumns([]);
                setLastFetchedDataSet('');
                return;
            }

            const fullCollectionName = selectedDataSet.collectionName;
            const parts = fullCollectionName.split('-');
            const baseCollectionName = parts.length >= 3 ? parts.slice(2).join('-') : fullCollectionName;

            Logger.debug('Templates.tsx', `Fetching schema for dataset: ${dataSetId}, full collection: ${fullCollectionName}, base collection: ${baseCollectionName}`);
            
            // Use ApiClient instead of fetch (automatically includes Authorization header)
            const data = await apiClient.get(`/iu/ds/cmp/set/entry/schema/${encodeURIComponent(baseCollectionName)}`);
            const schema = data.data;
            
            // Extract available columns (excluding system fields)
            const columns = Object.keys(schema.paths || {}).filter(key => 
                !['_id', 'createdAt', 'updatedAt', '__v'].includes(key)
            );
            setAvailableColumns(columns);
            setLastFetchedDataSet(dataSetId);
            Logger.debug('Templates.tsx', `Available columns: ${columns.join(', ')}`);
        } catch (error) {
            Logger.error('Templates.tsx', `Failed to fetch dataset schema: ${error}`);
            setAvailableColumns([]);
            setLastFetchedDataSet('');
        } finally {
            setLoadingColumns(false);
        }
    };

    const closeCreateTemplateDialog = () => {
        setCreateTemplateDialogOpen(false);
    };

    const closeRegisterEmailDialog = () => {
        setRegisterEmailDialogOpen(false);
    };

    const closeEmailEditDialog = () => {
        setEmailEditDialogOpen(false);
        setEditingEmailTemplate(null);
    };

    const handleEmailTemplateSuccess = () => {
        setTemplateDataRefreshKey(prev => prev + 1); // Trigger DataTable refresh
    };

    const handleCreateTemplate = (templateType: string) => {
        closeCreateTemplateDialog();
        switch (templateType) {
            case 'whatsapp':
                openWhatsAppDialog();
                break;
            case 'whatsappbusiness':
                openWhatsAppBusinessDialog();
                break;
            case 'telegram':
                openTelegramDialog();
                break;
            case 'email':
                openEmailDialog();
                break;
            case 'sms':
                openSMSDialog();
                break;
            default:
                openDialog();
        }
    };

      
    // Callback for DataTable's onEdit
    const handleDataTableEdit = (record: any) => {
        if (record.templateType === 'whatsapp') {
            openWhatsAppDialog(record);
        } else if (record.templateType === 'whatsappbusiness') {
            openWhatsAppBusinessDialog(record);
        } else if (record.templateType === 'telegram') {
            openTelegramDialog(record);
        } else if (record.templateType === 'mail') {
            openEmailDialog(record);
        } else if (record.templateType === 'sms') {
            openSMSDialog(record);
        } else {
            openDialog(record);
        }
    };
    const handleDataTableEditRef = useRef(handleDataTableEdit);
    handleDataTableEditRef.current = handleDataTableEdit;

    // Callback for DataTable's onDelete - shows confirmation dialog
    const handleDataTableDelete = (record: any) => {
        setTemplateToDelete(record);
        setDeleteConfirmDialogOpen(true);
    };

    const handleTemplateTileTouchStart = useCallback(
        (e: React.TouchEvent, tmpl: any) => {
            if (!isMobile) return;
            const t = e.touches[0];
            setTemplateTileSwipeRow((prev) => {
                const anotherWasOpen = Boolean(
                    prev && prev.tileId !== tmpl._id && Math.abs(prev.offset) > 0.5
                );
                const baseOffset =
                    !anotherWasOpen && prev?.tileId === tmpl._id ? prev!.offset : 0;
                templateSwipeSessionRef.current = {
                    tileId: tmpl._id,
                    startX: t.clientX,
                    startY: t.clientY,
                    baseOffset,
                    lock: null,
                };
                templateLatestSwipeOffsetRef.current = baseOffset;
                return { tileId: tmpl._id, offset: baseOffset, dragging: true };
            });
        },
        [isMobile]
    );

    const handleTemplateTileTouchMove = useCallback(
        (e: React.TouchEvent, tmpl: any, rowCanEdit: boolean, rowCanDelete: boolean) => {
            if (!isMobile) return;
            const sess = templateSwipeSessionRef.current;
            if (!sess || sess.tileId !== tmpl._id) return;
            const touch = e.touches[0];
            const dx = touch.clientX - sess.startX;
            const dy = touch.clientY - sess.startY;

            if (sess.lock === null) {
                if (
                    Math.abs(dx) > SWIPE_DIRECTION_LOCK_PX &&
                    Math.abs(dx) > Math.abs(dy) * 1.2
                ) {
                    sess.lock = 'h';
                } else if (
                    Math.abs(dy) > SWIPE_DIRECTION_LOCK_PX &&
                    Math.abs(dy) > Math.abs(dx) * 1.2
                ) {
                    sess.lock = 'v';
                }
            }
            if (sess.lock !== 'h') return;

            const raw = sess.baseOffset + dx;
            const clamped = clampSwipeOffset(
                raw,
                rowCanEdit,
                rowCanDelete,
                SWIPE_ACTION_WIDTH_PX
            );
            templateLatestSwipeOffsetRef.current = clamped;
            setTemplateTileSwipeRow({
                tileId: tmpl._id,
                offset: clamped,
                dragging: true,
            });
        },
        [isMobile]
    );

    const handleTemplateTileTouchEnd = useCallback(
        (
            e: React.TouchEvent,
            tmpl: any,
            rowCanEdit: boolean,
            rowCanDelete: boolean
        ) => {
            if (!isMobile) return;
            const sess = templateSwipeSessionRef.current;
            if (!sess || sess.tileId !== tmpl._id) {
                templateSwipeSessionRef.current = null;
                return;
            }
            const t = e.changedTouches[0];
            const dx = t.clientX - sess.startX;
            const dy = t.clientY - sess.startY;
            const { lock, baseOffset } = sess;
            templateSwipeSessionRef.current = null;

            const finishHorizontalSnap = (off: number) => {
                const snapped = computeSnappedOffset(
                    off,
                    rowCanEdit,
                    rowCanDelete,
                    SWIPE_ACTION_WIDTH_PX
                );
                templateLatestSwipeOffsetRef.current = snapped;
                setTemplateTileSwipeRow({
                    tileId: tmpl._id,
                    offset: snapped,
                    dragging: false,
                });
            };

            if (
                lock === null &&
                Math.abs(dx) > Math.abs(dy) * 1.2 &&
                Math.abs(dx) > 18
            ) {
                const raw = baseOffset + dx;
                const clamped = clampSwipeOffset(
                    raw,
                    rowCanEdit,
                    rowCanDelete,
                    SWIPE_ACTION_WIDTH_PX
                );
                finishHorizontalSnap(clamped);
                return;
            }

            if (lock !== 'h') {
                if (Math.abs(dx) < 12 && Math.abs(dy) < 12) {
                    if (Math.abs(baseOffset) > 0.5) {
                        templateLatestSwipeOffsetRef.current = 0;
                        setTemplateTileSwipeRow({
                            tileId: tmpl._id,
                            offset: 0,
                            dragging: false,
                        });
                    } else if (tmpl.thumbnailUrl) {
                        setThumbnailPreview(tmpl.thumbnailUrl);
                    }
                } else {
                    templateLatestSwipeOffsetRef.current = baseOffset;
                    setTemplateTileSwipeRow({
                        tileId: tmpl._id,
                        offset: baseOffset,
                        dragging: false,
                    });
                }
                return;
            }

            finishHorizontalSnap(templateLatestSwipeOffsetRef.current);
        },
        [isMobile]
    );

    const handleTemplateTileTouchCancel = useCallback(
        (
            _e: React.TouchEvent,
            tmpl: any,
            rowCanEdit: boolean,
            rowCanDelete: boolean
        ) => {
            if (!isMobile) return;
            const sess = templateSwipeSessionRef.current;
            if (!sess || sess.tileId !== tmpl._id) return;
            templateSwipeSessionRef.current = null;
            const snapped = computeSnappedOffset(
                templateLatestSwipeOffsetRef.current,
                rowCanEdit,
                rowCanDelete,
                SWIPE_ACTION_WIDTH_PX
            );
            templateLatestSwipeOffsetRef.current = snapped;
            setTemplateTileSwipeRow({
                tileId: tmpl._id,
                offset: snapped,
                dragging: false,
            });
        },
        [isMobile]
    );

    // Confirm delete handler
    const handleConfirmDelete = async () => {
        if (!templateToDelete) return;
        const templateId = typeof templateToDelete === 'string' ? templateToDelete : templateToDelete?._id;
        if (templateId) {
            setDeleteLoading(true);
            await deleteTemplate(templateId);
            setDeleteLoading(false);
        }
        setDeleteConfirmDialogOpen(false);
        setTemplateToDelete(null);
    };

    // Cancel delete handler
    const handleCancelDelete = () => {
        setDeleteConfirmDialogOpen(false);
        setTemplateToDelete(null);
    };

    const getSystemTemplateDisplayName = useCallback((fullName: string) => {
        if (!fullName) return '';
        const lower = fullName.toLowerCase();
        return lower.startsWith('iust-') ? fullName.substring(5) : fullName;
    }, []);

    const handleSystemTemplatesScroll = useCallback((direction: 'left' | 'right') => {
        const el = systemTemplatesScrollRef.current;
        if (!el) return;
        const scrollAmount = 400;
        el.scrollBy({ left: direction === 'left' ? -scrollAmount : scrollAmount, behavior: 'smooth' });
    }, []);

    const handleCreateFromSystemTemplate = useCallback((sysTemplate: any) => {
        setSystemTemplatePreview(null);
        setBrowseSystemTemplatesOpen(false);
        const templateType = (sysTemplate.templateType || '').toLowerCase();
        const sysId = sysTemplate._id || null;

        if (templateType === 'whatsapp') {
            setCurrentTemplate({
                companyId: user.companyId,
                templateName: '',
                templateType: 'whatsapp',
                templateContent: sysTemplate.templateContent || '',
                templateId: sysTemplate.templateId || undefined,
                category: sysTemplate.category || undefined,
                keywords: sysTemplate.keywords?.length ? [...sysTemplate.keywords] : undefined,
                collectionId: '',
                accessLevel: 'public',
            });
            setIsEditing(false);
            setPreselectedSystemTemplateId(sysId);
            setDialogOpen(true);
            setTemplateNameError('');
        } else if (templateType === 'sms') {
            setCurrentTemplate({
                companyId: user.companyId,
                templateName: '',
                templateType: 'sms',
                templateContent: '',
                templateId: sysTemplate.templateId || undefined,
                category: sysTemplate.category || undefined,
                keywords: sysTemplate.keywords?.length ? [...sysTemplate.keywords] : undefined,
                collectionId: '',
                accessLevel: 'public',
            });
            setIsEditing(false);
            setPreselectedSystemTemplateId(sysId);
            setDialogOpen(true);
            setTemplateNameError('');
        } else if (templateType === 'telegram') {
            setCurrentTemplate({
                companyId: user.companyId,
                templateName: '',
                templateType: 'telegram',
                templateContent: sysTemplate.templateContent || '',
                templateId: sysTemplate.templateId || undefined,
                category: sysTemplate.category || undefined,
                keywords: sysTemplate.keywords?.length ? [...sysTemplate.keywords] : undefined,
                collectionId: '',
                accessLevel: 'public',
            });
            setIsEditing(false);
            setPreselectedSystemTemplateId(sysId);
            setDialogOpen(true);
            setTemplateNameError('');
        } else if (templateType === 'whatsappbusiness') {
            setCurrentTemplate({
                companyId: user.companyId,
                templateName: '',
                templateType: 'whatsappbusiness',
                templateContent: sysTemplate.templateContent || '',
                templateId: sysTemplate.templateId || undefined,
                category: sysTemplate.category || undefined,
                keywords: sysTemplate.keywords?.length ? [...sysTemplate.keywords] : undefined,
                collectionId: '',
                accessLevel: 'public',
                whatsappBusinessConfig: sysTemplate.whatsappBusinessConfig ? { ...sysTemplate.whatsappBusinessConfig } : {
                    language: 'en_US', header: { type: 'none' }, footer: '', buttons: [],
                },
            });
            setIsEditing(false);
            setPreselectedSystemTemplateId(sysId);
            setDialogOpen(true);
            setTemplateNameError('');
        } else if (templateType === 'mail') {
            setPrefillEmailContent({
                content: sysTemplate.templateContent || '',
                subject: sysTemplate.emailSubject || '',
                builderConfig: sysTemplate.emailBuilderConfig || null,
            });
            setPreselectedSystemTemplateId(sysId);
            setRegisterEmailDialogOpen(true);
            setTemplateNameError('');
        }
    }, [user.companyId]);

    const handleRefreshMetaStatuses = useCallback(async () => {
        try {
            setRefreshingMetaStatus(true);
            await apiClient.post('/iu/contenttemplate/meta/refresh-all');
            setTemplateDataRefreshKey(k => k + 1);
        } catch (err) {
            Logger.error('Templates.tsx', `Failed to refresh Meta statuses: ${err}`);
        } finally {
            setRefreshingMetaStatus(false);
        }
    }, []);

    // Helper function for TemplateTable's columnRenderers, to be passed directly.
    const getDisplayTemplateName = useCallback((fullTemplateName: string | undefined) => {
        if (!fullTemplateName) return "";
        const parts = fullTemplateName.split('-');
        if (parts.length >= 3 && parts[0] === 'IUT' && parts[1] === user.companyId) {
            return parts.slice(2).join('-');
        }
        return fullTemplateName;
    }, [user.companyId]);

    const getDisplayCollectionName = useCallback((fullCollectionName: string | undefined) => {
        if (!fullCollectionName) return "";
        const parts = fullCollectionName.split('-');
        if (parts.length >= 3 && parts[0] === 'IU' && parts[1] === user.companyId) {
            return parts.slice(2).join('-');
        }
        return fullCollectionName;
    }, [user.companyId]);

    const templateColumnRenderers = useMemo(() => ({
        templateName: (row: Template) => getDisplayTemplateName(row.templateName),
        estimatedCreditCost: (row: Template) => {
            const v = (row as any).estimatedCreditCost;
            if (typeof v !== 'number' || Number.isNaN(v)) {
                return <Typography variant="caption" color="text.secondary">—</Typography>;
            }
            return (
                <Chip
                    icon={<LocalOffer sx={{ fontSize: 18 }} />}
                    label={v}
                    size="small"
                    sx={{
                        fontWeight: 800,
                        color: 'success.main',
                        bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.45)' : 'rgba(255,255,255,0.96)',
                        border: '1px solid',
                        borderColor: 'success.main',
                        height: 26,
                        boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
                        '& .MuiChip-icon': { ml: 0.5, color: 'success.main' },
                        '& .MuiChip-label': { px: 0.75, fontSize: '0.75rem' },
                    }}
                />
            );
        },
        collectionId: (row: Template) => {
            const dataSet = dataSets.find(ds => ds._id === row.collectionId);
            return dataSet ? getDisplayCollectionName(dataSet.collectionName) : row.collectionId;
        },
        templateUpdatedOn: (row: Template) => {
            if (row.templateUpdatedOn) {
                const date = new Date(row.templateUpdatedOn);
                return date.toLocaleString('en-GB', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                    second: '2-digit',
                    timeZoneName: 'short',
                    hour12: false,
                });
            }
            return '';
        },
        templateType: (row: Template) => {
            const templateType = row.templateType?.toLowerCase() || '';
            let buttonProps = {
                background: '',
                hoverBackground: '',
                color: 'primary' as any,
                text: templateType.toUpperCase()
            };

            switch (templateType) {
                case 'whatsapp':
                    buttonProps = {
                        background: 'linear-gradient(135deg, #2e7d32 0%, #1b5e20 100%)',
                        hoverBackground: 'linear-gradient(135deg, #388e3c 0%, #2e7d32 100%)',
                        color: 'success',
                        text: 'WHATSAPP'
                    };
                    break;
                case 'telegram':
                    buttonProps = {
                        background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                        hoverBackground: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                        color: 'info',
                        text: 'TELEGRAM'
                    };
                    break;
                case 'mail':
                    buttonProps = {
                        background: 'linear-gradient(135deg, #ed6c02 0%, #e65100 100%)',
                        hoverBackground: 'linear-gradient(135deg, #f57c00 0%, #ed6c02 100%)',
                        color: 'warning',
                        text: 'EMAIL'
                    };
                    break;
                case 'sms':
                    buttonProps = {
                        background: 'linear-gradient(135deg, #9c27b0 0%, #7b1fa2 100%)',
                        hoverBackground: 'linear-gradient(135deg, #9c27b0 0%, #7b1fa2 100%)',
                        color: 'secondary',
                        text: 'SMS'
                    };
                    break;
                case 'whatsappbusiness':
                    buttonProps = {
                        background: 'linear-gradient(135deg, #25d366 0%, #128c7e 100%)',
                        hoverBackground: 'linear-gradient(135deg, #2e7d32 0%, #1b5e20 100%)',
                        color: 'success',
                        text: 'WA BUSINESS'
                    };
                    break;
                default:
                    buttonProps = {
                        background: 'linear-gradient(135deg, #757575 0%, #616161 100%)',
                        hoverBackground: 'linear-gradient(135deg, #757575 0%, #616161 100%)',
                        color: 'default',
                        text: templateType.toUpperCase()
                    };
            }

            return (
                <Button
                    variant="contained"
                    color={buttonProps.color}
                    sx={{
                        background: buttonProps.background,
                        color: 'common.white',
                        padding: "2px 8px",
                        fontSize: "11px",
                        boxShadow: "none",
                        textTransform: "none",
                        minWidth: "auto",
                        height: "24px",
                        fontWeight: "bold",
                        '&:hover': {
                            background: buttonProps.hoverBackground,
                        }
                    }}
                >
                    {buttonProps.text}
                </Button>
            );
        },
        accessLevel: (row: Template) => {
            const accessLevel = row.accessLevel?.toLowerCase() || '';
            return (
                <Button
                    variant="contained"
                    color={accessLevel === 'private' ? 'success' : 'error'}
                    sx={{
                        padding: "2px 8px",
                        fontSize: "11px",
                        boxShadow: "none",
                        textTransform: "capitalize",
                        minWidth: "auto",
                        height: "24px"
                    }}
                >
                    {accessLevel}
                </Button>
            );
        },
        status: (row: Template) => {
            const type = (row.templateType || '').toLowerCase();
            if (type === 'whatsappbusiness') {
                const metaStatus = (row as any).metaStatus;
                if (!metaStatus) return <Typography variant="caption" color="text.secondary">—</Typography>;
                const colorMap: Record<string, 'success' | 'error' | 'warning' | 'info'> = { APPROVED: 'success', REJECTED: 'error', PENDING: 'warning', PAUSED: 'info', DISABLED: 'error' };
                return <Chip label={metaStatus} size="small" color={colorMap[metaStatus] || 'default'} sx={{ fontSize: '0.7rem', fontWeight: 700, height: 22 }} />;
            }
            const isApplicable = type === 'mail' || type === 'sms';
            if (!isApplicable) return '';
            const approvedField = (row as any).isApproved;
            let label = 'Pending Approval';
            let color: any = 'warning';
            if (approvedField === true) {
                label = 'Approved';
                color = 'success';
            } else if (approvedField === false) {
                label = 'Rejected';
                color = 'error';
            }
            return (
                <Button
                    variant="contained"
                    color={color}
                    sx={{
                        padding: "2px 8px",
                        fontSize: "11px",
                        boxShadow: "none",
                        textTransform: "none",
                        minWidth: "auto",
                        height: "24px",
                        color: 'common.white'
                    }}
                >
                    {label}
                </Button>
            );
        },
    }), [dataSets, getDisplayTemplateName, getDisplayCollectionName]);


    return (loading ?
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '256px' }}>
            <Backdrop
            sx={(theme: import('@mui/material/styles').Theme) => ({
                color: theme.palette.loading?.main ?? theme.palette.secondary.main,
                zIndex: theme.zIndex.drawer + 1,
            })}
            open={loading}
            >
            <CircularProgress color="inherit" />
            </Backdrop>
        </Box>
        :
        <Box pl={{ xs: 1.25, md: 2.5 }} pt={1} width="100%" mb={3}>
            <DynamicBreadcrumbs
                items={[
                    { itemPath: "/dashboard", itemLabel: "Home" },
                    { itemPath: "/instant-updates/templates", itemLabel: "Templates" },
                ]}
            />
            {/* Tabs for template types */}
            <Box sx={{ mb: 0, px: 0 }}>
                <Tabs
                    value={activeTab}
                    onChange={(_e, v) => {
                        setActiveTab(v);
                        setCategoryFilter('');
                        try {
                            localStorage.setItem(TEMPLATES_ACTIVE_TAB_STORAGE_KEY, v);
                        } catch {
                            /* ignore quota / private mode */
                        }
                    }}
                    variant="scrollable"
                    scrollButtons="auto"
                    allowScrollButtonsMobile
                    sx={{
                        minHeight: 0,
                        p: 0,
                        m: 0,
                        '& .MuiTabs-scroller': { p: 0, m: 0 },
                        '& .MuiTabs-flexContainer': { gap: 0, p: 0, m: 0 },
                        '& .MuiTabs-scrollButtons.Mui-disabled': { display: 'none' },
                        '& .MuiTabs-indicator': { backgroundColor: 'secondary.main' }
                    }}
                >
                    {visibleTabs.map(tab => (
                        <Tab 
                            key={tab.value} 
                            value={tab.value}
                            iconPosition="start"
                            icon={tab.icon}
                            label={(tab.premium && !isTabLicensed(tab.value)) ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography component="span">{tab.label}</Typography>
                                    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, px: 0.75, py: 0.25, borderRadius: '8px', backgroundColor: 'rgb(255, 215, 0)' }}>
                                        <LockKeyhole size={12} color={'rgb(139, 69, 19)'} />
                                        <Typography variant="caption" sx={{ color: 'rgb(139, 69, 19)', fontWeight: 700 }}>Premium</Typography>
                                    </Box>
                                </Box>
                            ) : tab.label}
                            sx={{
                                textTransform: 'none',
                                fontWeight: 600,
                                borderRadius: 1,
                                border: '1px solid',
                                borderColor: 'divider',
                                mr: 0.5,
                                px: 1.5,
                                minHeight: 40,
                                color: activeTab === tab.value ? 'common.white' : 'text.primary',
                                background: activeTab === tab.value ? tab.gradient : 'transparent',
                                '&.Mui-selected': {
                                    color: 'common.white'
                                },
                                '&:hover': {
                                    background: activeTab === tab.value ? tab.hoverGradient : 'action.hover'
                                },
                                '&:first-of-type': { ml: 0 }
                            }}
                        />
                    ))}
                </Tabs>
            </Box>
            
            {/* Per-Tab Containers with their own tables - only render active tab to avoid extra requests */}
            {visibleTabs.map((tab) => (
                activeTab === tab.value ? (
                    <Box key={`panel-${tab.value}`}>
                        <Box 
                            sx={{
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 1,
                                p: 2,
                                backgroundColor: 'background.paper',
                                mb: 2
                            }}
                        >
                            {/* Mobile: full-width New Template button */}
                            <Box sx={{ display: { xs: 'block', sm: 'none' }, mb: 1.5 }}>
                                {tab.value === 'whatsapp' ? (
                                    (canManageTemplates() || isUserSuperAdmin) ? (
                                        <Button variant="contained" color="success" startIcon={<WhatsApp />}
                                            onClick={() => openWhatsAppDialog()} fullWidth
                                            sx={{
                                                background: 'linear-gradient(135deg, #2e7d32 0%, #1b5e20 100%)',
                                                '&:hover': { background: 'linear-gradient(135deg, #388e3c 0%, #2e7d32 100%)' },
                                                fontWeight: 700, py: 1.2,
                                                border: 2, borderColor: 'secondary.main',
                                                boxShadow: '0 3px 10px rgba(46, 125, 50, 0.35)',
                                                animation: 'glowSoft-green 2.5s ease-in-out infinite',
                                                '@keyframes glowSoft-green': {
                                                    '0%, 100%': { boxShadow: '0 3px 10px rgba(46, 125, 50, 0.35), 0 0 6px rgba(46, 125, 50, 0.2)' },
                                                    '50%': { boxShadow: '0 3px 14px rgba(46, 125, 50, 0.55), 0 0 18px rgba(46, 125, 50, 0.35)' },
                                                },
                                            }}>
                                            New
                                        </Button>
                                    ) : null
                                ) : tab.value === 'mail' ? (
                                    (canManageTemplates() || isUserSuperAdmin) ? (
                                        <Button variant="contained" color="warning" startIcon={<Email />}
                                            onClick={() => openEmailDialog()} fullWidth
                                            sx={{
                                                background: 'linear-gradient(135deg, #ed6c02 0%, #e65100 100%)',
                                                '&:hover': { background: 'linear-gradient(135deg, #f57c00 0%, #ed6c02 100%)' },
                                                fontWeight: 700, py: 1.2,
                                                border: 2, borderColor: 'secondary.main',
                                                boxShadow: '0 3px 10px rgba(237, 108, 2, 0.35)',
                                                animation: 'glowSoft-orange 2.5s ease-in-out infinite',
                                                '@keyframes glowSoft-orange': {
                                                    '0%, 100%': { boxShadow: '0 3px 10px rgba(237, 108, 2, 0.35), 0 0 6px rgba(237, 108, 2, 0.2)' },
                                                    '50%': { boxShadow: '0 3px 14px rgba(237, 108, 2, 0.55), 0 0 18px rgba(237, 108, 2, 0.35)' },
                                                },
                                            }}>
                                            New
                                        </Button>
                                    ) : null
                                ) : tab.value === 'sms' ? (
                                    (canManageTemplates() || isUserSuperAdmin) && hasSmsLicense ? (
                                        <Button variant="contained" color="secondary" startIcon={<Sms />}
                                            onClick={() => openSMSDialog()} fullWidth
                                            sx={{
                                                background: 'linear-gradient(135deg, #9c27b0 0%, #7b1fa2 100%)',
                                                color: 'common.white',
                                                '&:hover': { background: 'linear-gradient(135deg, #9c27b0 0%, #7b1fa2 100%)' },
                                                fontWeight: 700, py: 1.2,
                                                border: 2, borderColor: 'secondary.main',
                                                boxShadow: '0 3px 10px rgba(156, 39, 176, 0.35)',
                                                animation: 'glowSoft-purple 2.5s ease-in-out infinite',
                                                '@keyframes glowSoft-purple': {
                                                    '0%, 100%': { boxShadow: '0 3px 10px rgba(156, 39, 176, 0.35), 0 0 6px rgba(156, 39, 176, 0.2)' },
                                                    '50%': { boxShadow: '0 3px 14px rgba(156, 39, 176, 0.55), 0 0 18px rgba(156, 39, 176, 0.35)' },
                                                },
                                            }}>
                                            New
                                        </Button>
                                    ) : null
                                ) : tab.value === 'telegram' ? (
                                    (canManageTemplates() || isUserSuperAdmin) && hasTelegramLicense ? (
                                        <Button variant="contained" color="info" startIcon={<Telegram />}
                                            onClick={() => openTelegramDialog()} fullWidth
                                            sx={{
                                                background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                                                '&:hover': { background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)' },
                                                fontWeight: 700, py: 1.2,
                                                border: 2, borderColor: 'secondary.main',
                                                boxShadow: '0 3px 10px rgba(25, 118, 210, 0.35)',
                                                animation: 'glowSoft-blue 2.5s ease-in-out infinite',
                                                '@keyframes glowSoft-blue': {
                                                    '0%, 100%': { boxShadow: '0 3px 10px rgba(25, 118, 210, 0.35), 0 0 6px rgba(25, 118, 210, 0.2)' },
                                                    '50%': { boxShadow: '0 3px 14px rgba(25, 118, 210, 0.55), 0 0 18px rgba(25, 118, 210, 0.35)' },
                                                },
                                            }}>
                                            New
                                        </Button>
                                    ) : null
                                ) : tab.value === 'whatsappbusiness' ? (
                                    (canManageTemplates() || isUserSuperAdmin) && hasWabizLicense ? (
                                        <Button variant="contained" color="success" startIcon={<WhatsApp />}
                                            onClick={() => openWhatsAppBusinessDialog()} fullWidth
                                            sx={{
                                                background: 'linear-gradient(135deg, #25d366 0%, #128c7e 100%)',
                                                '&:hover': { background: 'linear-gradient(135deg, #128c7e 0%, #075e54 100%)' },
                                                fontWeight: 700, py: 1.2,
                                                border: 2, borderColor: 'secondary.main',
                                                boxShadow: '0 3px 10px rgba(37, 211, 102, 0.35)',
                                                animation: 'glowSoft-teal 2.5s ease-in-out infinite',
                                                '@keyframes glowSoft-teal': {
                                                    '0%, 100%': { boxShadow: '0 3px 10px rgba(37, 211, 102, 0.35), 0 0 6px rgba(37, 211, 102, 0.2)' },
                                                    '50%': { boxShadow: '0 3px 14px rgba(37, 211, 102, 0.55), 0 0 18px rgba(37, 211, 102, 0.35)' },
                                                },
                                            }}>
                                            New
                                        </Button>
                                    ) : null
                                ) : null}
                            </Box>
                            {/* Toolbar: Search | Sort by, Order by, View toggle, New button */}
                            <Box display="flex" alignItems="center" gap={1} sx={{
                                mb: 1.5, p: 1.5, borderRadius: 2,
                                backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.45)' : 'rgba(0,0,0,0.06)',
                            }} flexWrap="wrap">
                                <TextField
                                    size="small"
                                    placeholder={viewMode === 'tile' ? 'Search templates...' : 'Search across all columns...'}
                                    value={viewMode === 'tile' ? tileSearchQuery : tableSearchQuery}
                                    onChange={(e) => viewMode === 'tile' ? setTileSearchQuery(e.target.value) : setTableSearchQuery(e.target.value)}
                                    sx={{ flexGrow: 1, maxWidth: { xs: 'none', sm: 240 } }}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Search fontSize="small" color="action" />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                                <FormControl size="small" sx={{ display: { xs: 'none', sm: 'inline-flex' }, minWidth: 140 }}>
                                    <Select
                                        value={categoryFilter}
                                        onChange={(e) => setCategoryFilter(e.target.value)}
                                        displayEmpty
                                        sx={{ fontSize: '0.82rem' }}
                                    >
                                        <MenuItem value="">Category: All</MenuItem>
                                        {tab.value === 'whatsappbusiness' ? [
                                            <MenuItem key="MARKETING" value="MARKETING">Category: Marketing</MenuItem>,
                                            <MenuItem key="UTILITY" value="UTILITY">Category: Utility</MenuItem>,
                                            <MenuItem key="AUTHENTICATION" value="AUTHENTICATION">Category: Authentication</MenuItem>,
                                        ] : [
                                            <MenuItem key="transactional" value="transactional">Category: Transactional</MenuItem>,
                                            <MenuItem key="promotional" value="promotional">Category: Promotional</MenuItem>,
                                            <MenuItem key="service" value="service">Category: Service</MenuItem>,
                                        ]}
                                    </Select>
                                </FormControl>
                                <Box sx={{ display: { xs: 'none', sm: 'flex' }, flex: 1 }} />
                                {viewMode === 'tile' && (
                                    <>
                                    <FormControl size="small" sx={{ display: { xs: 'none', sm: 'inline-flex' }, minWidth: 130 }}>
                                        <Select
                                            value={tileSortField}
                                            onChange={(e) => setTileSortField(e.target.value as 'name' | 'lastUpdated')}
                                            displayEmpty
                                            sx={{ fontSize: '0.82rem' }}
                                        >
                                            <MenuItem value="name">Sort by: Name</MenuItem>
                                            <MenuItem value="lastUpdated">Sort by: Last Updated</MenuItem>
                                        </Select>
                                    </FormControl>
                                    <FormControl size="small" sx={{ display: { xs: 'none', sm: 'inline-flex' }, minWidth: 110 }}>
                                        <Select
                                            value={tileSortOrder}
                                            onChange={(e) => setTileSortOrder(e.target.value as 'asc' | 'desc')}
                                            displayEmpty
                                            sx={{ fontSize: '0.82rem' }}
                                        >
                                            <MenuItem value="asc">ASC</MenuItem>
                                            <MenuItem value="desc">DESC</MenuItem>
                                        </Select>
                                    </FormControl>
                                    </>
                                )}
                                <ToggleButtonGroup
                                    value={viewMode}
                                    exclusive
                                    onChange={(_e, newMode) => {
                                        if (newMode !== null) {
                                            setViewMode(newMode);
                                            localStorage.setItem(VIEWMODE_STORAGE_KEY, newMode);
                                        }
                                    }}
                                    size="small"
                                    sx={{
                                        display: { xs: 'none', sm: 'inline-flex' },
                                        '& .MuiToggleButton-root': {
                                            border: '1px solid',
                                            borderColor: 'divider',
                                            '&.Mui-selected': {
                                                backgroundColor: (theme) => theme.palette.toggle?.selectedBg ?? (theme.palette.mode === 'dark' ? '#191f45' : theme.palette.secondary.main),
                                                color: (theme) => theme.palette.toggle?.selectedColor ?? (theme.palette.mode === 'dark' ? '#fff' : theme.palette.secondary.contrastText),
                                                '&:hover': { backgroundColor: (theme) => theme.palette.toggle?.selectedHoverBg ?? (theme.palette.mode === 'dark' ? '#232a54' : theme.palette.secondary.dark) }
                                            }
                                        }
                                    }}
                                >
                                    <ToggleButton value="tile" aria-label="Tile view">
                                        <Tooltip title="Tile View"><ViewModuleIcon fontSize="small" /></Tooltip>
                                    </ToggleButton>
                                    <ToggleButton value="table" aria-label="Table view">
                                        <Tooltip title="Table View"><TableRowsIcon fontSize="small" /></Tooltip>
                                    </ToggleButton>
                                </ToggleButtonGroup>
                                {(canManageTemplates() || isUserSuperAdmin) && tab.value === 'whatsapp' && (
                                    <Button variant="contained" color="success" startIcon={<WhatsApp />} onClick={() => openWhatsAppDialog()} size="small"
                                        sx={{ display: { xs: 'none', sm: 'inline-flex' }, background: 'linear-gradient(135deg, #2e7d32 0%, #1b5e20 100%)', '&:hover': { background: 'linear-gradient(135deg, #388e3c 0%, #2e7d32 100%)' }, whiteSpace: 'nowrap' }}>
                                        New
                                    </Button>
                                )}
                                {(tab.value === 'mail' || tab.value === 'sms') && (
                                    <Tooltip title="Refresh templates">
                                        <IconButton
                                            size="small"
                                            onClick={() => setTemplateDataRefreshKey(prev => prev + 1)}
                                            sx={{
                                                display: { xs: 'none', sm: 'inline-flex' },
                                                border: '1px solid', borderColor: 'divider', borderRadius: 1,
                                                '&:hover': { backgroundColor: 'action.hover' },
                                            }}
                                        >
                                            <Refresh fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                )}
                                {(canManageTemplates() || isUserSuperAdmin) && tab.value === 'mail' && (
                                    <Button variant="contained" color="warning" startIcon={<Email />} onClick={() => openEmailDialog()} size="small"
                                        sx={{ display: { xs: 'none', sm: 'inline-flex' }, background: 'linear-gradient(135deg, #ed6c02 0%, #e65100 100%)', '&:hover': { background: 'linear-gradient(135deg, #f57c00 0%, #ed6c02 100%)' }, whiteSpace: 'nowrap' }}>
                                        New
                                    </Button>
                                )}
                                {(canManageTemplates() || isUserSuperAdmin) && tab.value === 'sms' && hasSmsLicense && (
                                    <Button variant="contained" color="secondary" startIcon={<Sms />} onClick={() => openSMSDialog()} size="small"
                                        sx={{ display: { xs: 'none', sm: 'inline-flex' }, background: 'linear-gradient(135deg, #9c27b0 0%, #7b1fa2 100%)', color: 'common.white', '&:hover': { background: 'linear-gradient(135deg, #9c27b0 0%, #7b1fa2 100%)' }, whiteSpace: 'nowrap' }}>
                                        New
                                    </Button>
                                )}
                                {(canManageTemplates() || isUserSuperAdmin) && tab.value === 'telegram' && hasTelegramLicense && (
                                    <Button variant="contained" color="info" startIcon={<Telegram />} onClick={() => openTelegramDialog()} size="small"
                                        sx={{ display: { xs: 'none', sm: 'inline-flex' }, background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)', '&:hover': { background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)' }, whiteSpace: 'nowrap' }}>
                                        New
                                    </Button>
                                )}
                                {tab.value === 'whatsappbusiness' && hasWabizLicense && (
                                    <Tooltip title={refreshingMetaStatus ? 'Refreshing...' : 'Refresh Meta statuses'}>
                                        <IconButton
                                            size="small"
                                            onClick={handleRefreshMetaStatuses}
                                            disabled={refreshingMetaStatus}
                                            sx={{
                                                display: { xs: 'none', sm: 'inline-flex' },
                                                border: '1px solid', borderColor: 'divider', borderRadius: 1,
                                                '&:hover': { backgroundColor: 'action.hover' },
                                            }}
                                        >
                                            {refreshingMetaStatus ? <CircularProgress size={18} /> : <Refresh fontSize="small" />}
                                        </IconButton>
                                    </Tooltip>
                                )}
                                {(canManageTemplates() || isUserSuperAdmin) && tab.value === 'whatsappbusiness' && hasWabizLicense && (
                                    <Button variant="contained" color="success" startIcon={<WhatsApp />} onClick={() => openWhatsAppBusinessDialog()} size="small"
                                        sx={{ display: { xs: 'none', sm: 'inline-flex' }, background: 'linear-gradient(135deg, #25d366 0%, #128c7e 100%)', '&:hover': { background: 'linear-gradient(135deg, #128c7e 0%, #075e54 100%)' }, whiteSpace: 'nowrap' }}>
                                        New
                                    </Button>
                                )}
                                {systemTemplates.length > 0 && canShowAiTemplatesForTabValue(tab.value) && (
                                    <Button
                                        variant="outlined"
                                        color="secondary"
                                        size="small"
                                        startIcon={<AutoAwesome />}
                                        onClick={() => setBrowseSystemTemplatesOpen(true)}
                                        sx={{ display: { xs: 'none', sm: 'inline-flex' }, whiteSpace: 'nowrap' }}
                                    >
                                        AI Templates
                                    </Button>
                                )}
                                <IconButton
                                    sx={{ display: { xs: 'flex', sm: 'none' }, ml: 'auto', border: '1px solid', borderColor: 'divider', '&:hover': { backgroundColor: 'action.hover' } }}
                                    onClick={() => setToolbarMenuOpen(true)}
                                    size="small"
                                >
                                    <MenuIcon />
                                </IconButton>
                            </Box>
                            {filteredTileTemplates.length === 0 && !tileLoading && (
                                <Box sx={{
                                    border: '1px dashed',
                                    borderColor: 'divider',
                                    borderRadius: 2,
                                    py: 4,
                                    mb: 1.5,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    gap: 1.5,
                                    backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(144,202,249,0.05)' : 'rgba(25,118,210,0.03)',
                                }}>
                                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', px: 2 }}>
                                        No templates available, feel free to browse through System Templates to create one.
                                    </Typography>
                                    {systemTemplates.length > 0 && canShowAiTemplatesForTabValue(tab.value) && (
                                        <Button
                                            variant="outlined"
                                            color="secondary"
                                            size="small"
                                            startIcon={<AutoAwesome />}
                                            onClick={() => setBrowseSystemTemplatesOpen(true)}
                                        >
                                            AI Templates
                                        </Button>
                                    )}
                                </Box>
                            )}
                            {((tab.value === 'sms' && !hasSmsLicense) || (tab.value === 'telegram' && !hasTelegramLicense) || (tab.value === 'whatsappbusiness' && !hasWabizLicense)) ? (
                                <Box sx={{
                                    p: 2,
                                    border: '1px dashed',
                                    borderColor: 'warning.main',
                                    borderRadius: 1,
                                    backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 193, 7, 0.1)' : 'rgba(255, 193, 7, 0.08)'
                                }}>
                                    {tab.value === 'sms' && (
                                        <>
                                            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 600 }}>
                                                <LockKeyhole size={16} /> SMS templates are available on Business, Business Plus, and Enterprise plans.
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                Please upgrade your plan to unlock SMS templates.
                                            </Typography>
                                        </>
                                    )}
                                    {tab.value === 'telegram' && (
                                        <>
                                            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 600 }}>
                                                <LockKeyhole size={16} /> Telegram templates are available on Business Plus and Enterprise plans.
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                Please upgrade your plan to unlock Telegram templates.
                                            </Typography>
                                        </>
                                    )}
                                    {tab.value === 'whatsappbusiness' && (
                                        <>
                                            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 600 }}>
                                                <LockKeyhole size={16} /> WhatsApp Business templates are available on Enterprise plan.
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                Please upgrade your plan to unlock WhatsApp Business templates.
                                            </Typography>
                                        </>
                                    )}
                                </Box>
                            ) : viewMode === 'table' ? (
                                <DataTableWithStaticSchema
                                    dataUrl={`/iu/contenttemplate/company/${tab.value}`}
                                    schemaObject={templateSchema}
                                    columnsToExclude={['_id', 'companyId', '__v', 'templateCreatedByUserId', 'templateUpdatedByUserId', 'createdAt', 'updatedAt', 'sharedTo', 'templateContent', 'emailSubject', ...(tab.value !== 'mail' && tab.value !== 'sms' && tab.value !== 'whatsappbusiness' ? ['status'] : [])]}
                                    onEdit={(canManageTemplates() || isUserSuperAdmin) ? handleDataTableEdit : undefined}
                                    onDelete={(canManageTemplates() || isUserSuperAdmin) ? handleDataTableDelete : undefined}
                                    columnRenderers={templateColumnRenderers}
                                    refreshTrigger={templateDataRefreshKey}
                                    hideSearch
                                    externalSearchQuery={tableSearchQuery}
                                />
                            ) : (
                                <Box>
                                    {tileLoading ? (
                                        <Box display="flex" justifyContent="center" py={4}>
                                            <CircularProgress size={32} />
                                        </Box>
                                    ) : filteredTileTemplates.length === 0 ? (
                                        <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                                            {tileSearchQuery ? 'No templates match your search.' : 'No templates found.'}
                                        </Typography>
                                    ) : (
                                        <>
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2.5, justifyContent: { xs: 'center', md: 'flex-start' } }}>
                                            {filteredTileTemplates.slice(0, tileVisibleCount).map((tmpl: any) => {
                                                const tabConf = getTabConfig(tmpl.templateType);
                                                const tmplType = (tmpl.templateType || '').toLowerCase();
                                                const hasApprovalStatus = tmplType === 'mail' || tmplType === 'sms';
                                                const approvalLabel = tmpl.isApproved === true ? 'Approved' : tmpl.isApproved === false ? 'Rejected' : 'Pending Approval';
                                                const approvalColor: 'success' | 'error' | 'warning' = tmpl.isApproved === true ? 'success' : tmpl.isApproved === false ? 'error' : 'warning';
                                                const canEditTile = (canManageTemplates() || isUserSuperAdmin);
                                                const canDeleteTile = canEditTile;
                                                const rowOffset =
                                                    isMobile && templateTileSwipeRow?.tileId === tmpl._id
                                                        ? templateTileSwipeRow!.offset
                                                        : 0;
                                                const rowDragging =
                                                    isMobile &&
                                                    templateTileSwipeRow?.tileId === tmpl._id &&
                                                    Boolean(templateTileSwipeRow?.dragging);

                                                const tileMedia = (
                                                    <>
                                                        {['sms', 'gsmtext'].includes(tmplType) &&
                                                            typeof tmpl.estimatedCreditCost === 'number' && (
                                                            <Chip
                                                                icon={<LocalOffer sx={{ fontSize: 18 }} />}
                                                                label={tmpl.estimatedCreditCost}
                                                                size="small"
                                                                sx={{
                                                                    position: 'absolute',
                                                                    top: 8,
                                                                    left: 8,
                                                                    zIndex: 2,
                                                                    fontWeight: 800,
                                                                    color: 'success.main',
                                                                    bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.65)' : 'rgba(255,255,255,0.96)',
                                                                    border: '1px solid',
                                                                    borderColor: 'success.main',
                                                                    height: 28,
                                                                    boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
                                                                    '& .MuiChip-icon': { ml: 0.5, color: 'success.main' },
                                                                    '& .MuiChip-label': { px: 0.75, fontSize: '0.8rem' },
                                                                }}
                                                            />
                                                        )}
                                                        {tmpl.thumbnailUrl ? (
                                                            <img
                                                                src={tmpl.thumbnailUrl}
                                                                alt=""
                                                                onClick={(e) => { e.stopPropagation(); setThumbnailPreview(tmpl.thumbnailUrl); }}
                                                                style={{
                                                                    position: 'absolute', top: 0, left: 0,
                                                                    width: '100%', height: '100%',
                                                                    objectFit: 'cover', objectPosition: 'top', cursor: 'pointer',
                                                                }}
                                                            />
                                                        ) : (
                                                            <Box sx={{
                                                                position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                                                                background: tabConf.gradient,
                                                                display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 0.18,
                                                            }}>
                                                                {React.cloneElement(tabConf.icon, { sx: { fontSize: 64, color: '#fff' } })}
                                                            </Box>
                                                        )}
                                                        {hasApprovalStatus && (
                                                            <Chip
                                                                label={approvalLabel}
                                                                color={approvalColor}
                                                                size="small"
                                                                sx={{
                                                                    position: 'absolute', top: 8, right: 8, zIndex: 2,
                                                                    height: 22, fontSize: '0.68rem', fontWeight: 700,
                                                                    color: '#fff',
                                                                    boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
                                                                }}
                                                            />
                                                        )}
                                                        {tmplType === 'whatsappbusiness' && (tmpl as any).metaStatus && (
                                                            <Chip
                                                                label={(tmpl as any).metaStatus}
                                                                color={(tmpl as any).metaStatus === 'APPROVED' ? 'success' : (tmpl as any).metaStatus === 'REJECTED' ? 'error' : 'warning'}
                                                                size="small"
                                                                sx={{
                                                                    position: 'absolute', top: 8, right: 8, zIndex: 2,
                                                                    height: 22, fontSize: '0.68rem', fontWeight: 700,
                                                                    color: '#fff',
                                                                    boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
                                                                }}
                                                            />
                                                        )}
                                                    </>
                                                );

                                                if (!isMobile) {
                                                    return (
                                                    <Box
                                                        key={tmpl._id}
                                                        sx={{
                                                            width: 260,
                                                            height: 200,
                                                            borderRadius: 2.5,
                                                            overflow: 'hidden',
                                                            position: 'relative',
                                                            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                                                            cursor: 'pointer',
                                                            flexShrink: 0,
                                                            border: '1px solid',
                                                            borderColor: 'divider',
                                                            '&:hover .tile-overlay': { height: 136 },
                                                            '&:hover .tile-details': { opacity: 1 },
                                                            '@media (hover: none)': {
                                                                '& .tile-overlay': { height: 136 },
                                                                '& .tile-details': { opacity: 1 },
                                                            },
                                                        }}
                                                    >
                                                        {tileMedia}
                                                        <Box
                                                            className="tile-overlay"
                                                            sx={{
                                                                position: 'absolute', bottom: 0, left: 0, right: 0,
                                                                overflow: 'hidden',
                                                                background: 'linear-gradient(to top, rgba(0,0,0,0.88) 0%, rgba(0,0,0,0.72) 50%, rgba(0,0,0,0.25) 100%)',
                                                                transition: 'height 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
                                                                height: 40, px: 1.5,
                                                                display: 'flex', flexDirection: 'column', justifyContent: 'flex-start',
                                                            }}
                                                        >
                                                            <Typography variant="subtitle2" noWrap sx={{
                                                                color: '#fff', fontWeight: 700, fontSize: '0.82rem',
                                                                lineHeight: '40px', textShadow: '0 1px 3px rgba(0,0,0,0.4)', flexShrink: 0,
                                                            }}>
                                                                {getDisplayTemplateName(tmpl.templateName)}
                                                            </Typography>
                                                            <Box className="tile-details" sx={{
                                                                opacity: 0, transition: 'opacity 0.3s ease 0.08s',
                                                                display: 'flex', flexDirection: 'column', gap: 0.5, pt: 0.25, pb: 1, flex: 1,
                                                            }}>
                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                                                    <Button variant="contained" size="small" sx={{
                                                                        background: tabConf.gradient, color: '#fff',
                                                                        padding: '1px 8px', fontSize: '10px', boxShadow: 'none',
                                                                        textTransform: 'none', minWidth: 'auto', height: 20, fontWeight: 'bold',
                                                                        '&:hover': { background: tabConf.hoverGradient }, pointerEvents: 'none',
                                                                    }}>
                                                                        {tabConf.label.toUpperCase()}
                                                                    </Button>
                                                                    <Button variant="contained" color={tmpl.accessLevel === 'private' ? 'success' : 'error'} size="small" sx={{
                                                                        padding: '1px 8px', fontSize: '10px', boxShadow: 'none',
                                                                        textTransform: 'capitalize', minWidth: 'auto', height: 20, pointerEvents: 'none',
                                                                    }}>
                                                                        {tmpl.accessLevel}
                                                                    </Button>
                                                                </Box>
                                                                {tmpl.templateUpdatedOn && (
                                                                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.68rem', lineHeight: 1.2 }}>
                                                                        Updated: {formatDate(tmpl.templateUpdatedOn)}
                                                                    </Typography>
                                                                )}
                                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 'auto' }}>
                                                                    {canEditTile && (
                                                                        <>
                                                                            <Tooltip title="Edit">
                                                                                <Button color="secondary" variant="contained" size="small"
                                                                                    onClick={(e) => { e.stopPropagation(); handleDataTableEdit(tmpl); }}
                                                                                    sx={{ borderRadius: 1, textTransform: 'none', px: 1.25, minWidth: 36 }}>
                                                                                    <Edit fontSize="small" />
                                                                                </Button>
                                                                            </Tooltip>
                                                                            <Tooltip title="Delete">
                                                                                <Button color="error" variant="contained" size="small"
                                                                                    onClick={(e) => { e.stopPropagation(); handleDataTableDelete(tmpl); }}
                                                                                    sx={{ borderRadius: 1, textTransform: 'none', px: 1.25, minWidth: 36 }}>
                                                                                    <Delete fontSize="small" />
                                                                                </Button>
                                                                            </Tooltip>
                                                                        </>
                                                                    )}
                                                                </Box>
                                                            </Box>
                                                        </Box>
                                                    </Box>
                                                    );
                                                }

                                                /* Mobile / tablet: iOS-style swipe — Edit (left), Delete (right); overlay shows name only */
                                                return (
                                                    <Box
                                                        key={tmpl._id}
                                                        sx={{
                                                            width: 260,
                                                            height: 200,
                                                            borderRadius: 2.5,
                                                            overflow: 'hidden',
                                                            position: 'relative',
                                                            flexShrink: 0,
                                                            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                                                            border: '1px solid',
                                                            borderColor: 'divider',
                                                            touchAction: 'pan-y',
                                                            isolation: 'isolate',
                                                        }}
                                                    >
                                                        <Box
                                                            sx={{
                                                                position: 'absolute',
                                                                inset: 0,
                                                                display: 'flex',
                                                                flexDirection: 'row',
                                                                alignItems: 'stretch',
                                                                zIndex: 0,
                                                                borderRadius: 2.5,
                                                                overflow: 'hidden',
                                                            }}
                                                        >
                                                            {canEditTile && (
                                                                <Box
                                                                    sx={{
                                                                        width: SWIPE_ACTION_WIDTH_PX,
                                                                        flexShrink: 0,
                                                                        bgcolor: 'secondary.main',
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'center',
                                                                    }}
                                                                >
                                                                    <Tooltip title="Edit">
                                                                        <IconButton
                                                                            aria-label="Edit template"
                                                                            size="medium"
                                                                            sx={{
                                                                                color: 'secondary.contrastText',
                                                                                '&:hover': { backgroundColor: 'rgba(0,0,0,0.12)' },
                                                                            }}
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                setTemplateTileSwipeRow(null);
                                                                                handleDataTableEditRef.current(tmpl);
                                                                            }}
                                                                        >
                                                                            <Edit fontSize="medium" />
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                </Box>
                                                            )}
                                                            <Box sx={{ flex: 1, minWidth: 0 }} />
                                                            {canDeleteTile && (
                                                                <Box
                                                                    sx={{
                                                                        width: SWIPE_ACTION_WIDTH_PX,
                                                                        flexShrink: 0,
                                                                        bgcolor: 'error.main',
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'center',
                                                                    }}
                                                                >
                                                                    <Tooltip title="Delete">
                                                                        <IconButton
                                                                            aria-label="Delete template"
                                                                            size="medium"
                                                                            sx={{
                                                                                color: 'error.contrastText',
                                                                                '&:hover': { backgroundColor: 'rgba(0,0,0,0.12)' },
                                                                            }}
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                setTemplateTileSwipeRow(null);
                                                                                handleDataTableDelete(tmpl);
                                                                            }}
                                                                        >
                                                                            <Delete fontSize="medium" />
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                </Box>
                                                            )}
                                                        </Box>

                                                        <Box
                                                            onTouchStart={(e) => handleTemplateTileTouchStart(e, tmpl)}
                                                            onTouchMove={(e) => handleTemplateTileTouchMove(e, tmpl, canEditTile, canDeleteTile)}
                                                            onTouchEnd={(e) => handleTemplateTileTouchEnd(e, tmpl, canEditTile, canDeleteTile)}
                                                            onTouchCancel={(e) => handleTemplateTileTouchCancel(e, tmpl, canEditTile, canDeleteTile)}
                                                            onClick={() => {
                                                                if (Math.abs(rowOffset) < 1 && tmpl.thumbnailUrl) {
                                                                    setThumbnailPreview(tmpl.thumbnailUrl);
                                                                }
                                                            }}
                                                            sx={{
                                                                position: 'relative',
                                                                zIndex: 1,
                                                                height: '100%',
                                                                display: 'flex',
                                                                flexDirection: 'column',
                                                                borderRadius: 2.5,
                                                                overflow: 'hidden',
                                                                cursor: 'pointer',
                                                                backgroundColor: 'background.paper',
                                                                transform: `translateX(${rowOffset}px)`,
                                                                transition: rowDragging
                                                                    ? 'none'
                                                                    : 'transform 0.35s cubic-bezier(0.25, 0.8, 0.25, 1)',
                                                                willChange: rowDragging ? 'transform' : 'auto',
                                                                boxShadow: rowOffset !== 0 ? 3 : 'none',
                                                            }}
                                                        >
                                                            <Box sx={{ position: 'relative', flex: 1, minHeight: 0 }}>
                                                                {tileMedia}
                                                            </Box>
                                                            <Box
                                                                sx={{
                                                                    background: 'linear-gradient(to top, rgba(0,0,0,0.92) 0%, rgba(0,0,0,0.75) 55%, rgba(0,0,0,0.2) 100%)',
                                                                    px: 1.5,
                                                                    py: 1.25,
                                                                    minHeight: 48,
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    flexShrink: 0,
                                                                }}
                                                            >
                                                                <Typography
                                                                    variant="subtitle2"
                                                                    sx={{
                                                                        color: '#fff',
                                                                        fontWeight: 700,
                                                                        fontSize: '0.88rem',
                                                                        lineHeight: 1.35,
                                                                        textShadow: '0 1px 4px rgba(0,0,0,0.45)',
                                                                        wordBreak: 'break-word',
                                                                    }}
                                                                >
                                                                    {getDisplayTemplateName(tmpl.templateName)}
                                                                </Typography>
                                                            </Box>
                                                        </Box>
                                                    </Box>
                                                );
                                            })}
                                        </Box>
                                        {tileVisibleCount < filteredTileTemplates.length && (
                                            <Box display="flex" justifyContent="center" mt={3}>
                                                <Button
                                                    variant="outlined"
                                                    color="secondary"
                                                    onClick={() => setTileVisibleCount(prev => prev + TILE_PAGE_SIZE)}
                                                >
                                                    Load More ({filteredTileTemplates.length - tileVisibleCount} remaining)
                                                </Button>
                                            </Box>
                                        )}
                                        </>
                                    )}
                                </Box>
                            )}
                        </Box>
                    </Box>
                ) : null
            ))}

            {/* Mobile Toolbar Dialog */}
            <Dialog open={toolbarMenuOpen} onClose={() => setToolbarMenuOpen(false)} fullWidth maxWidth="xs">
                <DialogTitle>Toolbar</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        <FormControl size="small" fullWidth>
                            <Select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)} displayEmpty sx={{ fontSize: '0.82rem' }}>
                                <MenuItem value="">Category: All</MenuItem>
                                {activeTab === 'whatsappbusiness' ? [
                                    <MenuItem key="MARKETING" value="MARKETING">Category: Marketing</MenuItem>,
                                    <MenuItem key="UTILITY" value="UTILITY">Category: Utility</MenuItem>,
                                    <MenuItem key="AUTHENTICATION" value="AUTHENTICATION">Category: Authentication</MenuItem>,
                                ] : [
                                    <MenuItem key="transactional" value="transactional">Category: Transactional</MenuItem>,
                                    <MenuItem key="promotional" value="promotional">Category: Promotional</MenuItem>,
                                    <MenuItem key="service" value="service">Category: Service</MenuItem>,
                                ]}
                            </Select>
                        </FormControl>
                        {viewMode === 'tile' && (
                            <>
                                <FormControl size="small" fullWidth>
                                    <Select value={tileSortField} onChange={(e) => setTileSortField(e.target.value as 'name' | 'lastUpdated')} displayEmpty sx={{ fontSize: '0.82rem' }}>
                                        <MenuItem value="name">Sort by: Name</MenuItem>
                                        <MenuItem value="lastUpdated">Sort by: Last Updated</MenuItem>
                                    </Select>
                                </FormControl>
                                <FormControl size="small" fullWidth>
                                    <Select value={tileSortOrder} onChange={(e) => setTileSortOrder(e.target.value as 'asc' | 'desc')} displayEmpty sx={{ fontSize: '0.82rem' }}>
                                        <MenuItem value="asc">ASC</MenuItem>
                                        <MenuItem value="desc">DESC</MenuItem>
                                    </Select>
                                </FormControl>
                            </>
                        )}
                        <Box display="flex" alignItems="center" gap={1}>
                            <Typography variant="body2" sx={{ mr: 1 }}>View:</Typography>
                            <ToggleButtonGroup
                                value={viewMode}
                                exclusive
                                onChange={(_e, newMode) => { if (newMode !== null) { setViewMode(newMode); localStorage.setItem(VIEWMODE_STORAGE_KEY, newMode); } }}
                                size="small"
                                sx={{
                                    '& .MuiToggleButton-root': {
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        '&.Mui-selected': {
                                            backgroundColor: (theme) => theme.palette.toggle?.selectedBg ?? (theme.palette.mode === 'dark' ? '#191f45' : theme.palette.secondary.main),
                                            color: (theme) => theme.palette.toggle?.selectedColor ?? (theme.palette.mode === 'dark' ? '#fff' : theme.palette.secondary.contrastText),
                                            '&:hover': { backgroundColor: (theme) => theme.palette.toggle?.selectedHoverBg ?? (theme.palette.mode === 'dark' ? '#232a54' : theme.palette.secondary.dark) }
                                        }
                                    }
                                }}
                            >
                                <ToggleButton value="tile"><ViewModuleIcon fontSize="small" /></ToggleButton>
                                <ToggleButton value="table"><TableRowsIcon fontSize="small" /></ToggleButton>
                            </ToggleButtonGroup>
                        </Box>
                        {(activeTab === 'mail' || activeTab === 'sms') && (
                            <Button variant="outlined" color="secondary" startIcon={<Refresh />} onClick={() => { setTemplateDataRefreshKey(prev => prev + 1); setToolbarMenuOpen(false); }} size="small" fullWidth>
                                Refresh
                            </Button>
                        )}
                        {systemTemplates.length > 0 && canShowAiTemplatesForTabValue(activeTab) && (
                            <Button
                                variant="outlined"
                                color="secondary"
                                startIcon={<AutoAwesome />}
                                onClick={() => { setBrowseSystemTemplatesOpen(true); setToolbarMenuOpen(false); }}
                                size="small"
                                fullWidth
                            >
                                AI Templates
                            </Button>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setToolbarMenuOpen(false)} variant="outlined" color="secondary">Close</Button>
                </DialogActions>
            </Dialog>

            {/* Create Template Dialog for Mobile */}
            <Dialog 
                open={createTemplateDialogOpen} 
                onClose={closeCreateTemplateDialog}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Create New Template</DialogTitle>
                <DialogContent>
                    <Box display="flex" flexDirection="column" gap={2} mt={1}>
                        <Button 
                            variant="contained" 
                            color="success" 
                            startIcon={<WhatsApp />} 
                            onClick={() => handleCreateTemplate('whatsapp')}
                            fullWidth
                            sx={{
                                background: 'linear-gradient(135deg, #2e7d32 0%, #1b5e20 100%)',
                                '&:hover': {
                                    background: 'linear-gradient(135deg, #388e3c 0%, #2e7d32 100%)',
                                },
                                py: 2
                            }}
                        >
                            New
                        </Button>
                        
                        <Button 
                            variant="contained" 
                            color="success" 
                            startIcon={hasWabizLicense ? <WhatsApp /> : <LockKeyhole size={16} />} 
                            disabled={!hasWabizLicense}
                            onClick={() => hasWabizLicense && handleCreateTemplate('whatsappbusiness')}
                            fullWidth
                            sx={{
                                background: 'linear-gradient(135deg, #25d366 0%, #128c7e 100%)',
                                ...(!hasWabizLicense ? { opacity: 0.7 } : {}),
                                '&:hover': {
                                    background: 'linear-gradient(135deg, #128c7e 0%, #075e54 100%)',
                                },
                                py: 2
                            }}
                        >
                            New
                        </Button>
                        
                        <Button 
                            variant="contained" 
                            color="info" 
                            startIcon={<Telegram />} 
                            onClick={() => handleCreateTemplate('telegram')}
                            fullWidth
                            sx={{
                                background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                                '&:hover': {
                                    background: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)',
                                },
                                py: 2
                            }}
                        >
                            New
                        </Button>
                        
                        <Button 
                            variant="contained" 
                            color="warning" 
                            startIcon={<Email />} 
                            onClick={() => handleCreateTemplate('email')}
                            fullWidth
                            sx={{
                                background: 'linear-gradient(135deg, #ed6c02 0%, #e65100 100%)',
                                '&:hover': {
                                    background: 'linear-gradient(135deg, #f57c00 0%, #ed6c02 100%)',
                                },
                                py: 2
                            }}
                        >
                            New
                        </Button>
                        
                        <Button 
                            variant="contained" 
                            color="secondary" 
                            startIcon={<LockKeyhole size={16} />} 
                            disabled
                            fullWidth
                            sx={{
                                background: 'linear-gradient(135deg, #9c27b0 0%, #7b1fa2 100%)',
                                opacity: 0.7,
                                '&:hover': {
                                    background: 'linear-gradient(135deg, #9c27b0 0%, #7b1fa2 100%)',
                                    opacity: 0.7,
                                },
                                position: 'relative',
                                '&::after': {
                                    content: '""',
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    background: 'rgba(255, 255, 255, 0.1)',
                                    borderRadius: 'inherit',
                                    pointerEvents: 'none'
                                },
                                py: 2
                            }}
                        >
                            New
                        </Button>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button 
                        onClick={closeCreateTemplateDialog} 
                        variant="outlined" 
                        color="secondary"
                    >
                        Cancel
                    </Button>
                </DialogActions>
            </Dialog>

            <TemplateDialog
                dialogOpen={dialogOpen}
                closeDialog={() => { closeDialog(); setPreselectedSystemTemplateId(null); }}
                isEditing={isEditing}
                currentTemplate={currentTemplate}
                setCurrentTemplate={setCurrentTemplate}
                saveTemplate={saveTemplate}
                dataSets={dataSets}
                templateSchema={templateSchema}
                templateNameError={templateNameError}
                setTemplateNameError={setTemplateNameError}
                companyId={user.companyId}
                saveError={saveError}
                setSaveError={setSaveError}
                preselectedSystemTemplateId={preselectedSystemTemplateId}
            />

            <RegisterEmailTemplate
                open={registerEmailDialogOpen}
                onClose={() => { closeRegisterEmailDialog(); setPrefillEmailContent(null); setPreselectedSystemTemplateId(null); }}
                onSuccess={handleEmailTemplateSuccess}
                dataSets={dataSets}
                getDisplayCollectionName={(name) => {
                    if (!name) return '';
                    return name.replace(/^IU-[^-]+-/, '');
                }}
                initialTemplateContent={prefillEmailContent?.content}
                initialEmailSubject={prefillEmailContent?.subject}
                initialEmailBuilderConfig={prefillEmailContent?.builderConfig}
                preselectedSystemTemplateId={preselectedSystemTemplateId}
            />

            <EmailTemplateDialog
                open={emailEditDialogOpen}
                onClose={closeEmailEditDialog}
                template={editingEmailTemplate}
                dataSets={dataSets}
                availableColumns={availableColumns}
                loadingColumns={loadingColumns}
                getDisplayCollectionName={(name) => {
                    if (!name) return '';
                    // Remove IU-<companyId>- prefix from display
                    return name.replace(/^IU-[^-]+-/, '');
                }}
                companyId={user.companyId}
                onSuccess={handleEmailTemplateSuccess}
                onDataSetChange={fetchDataSetColumns}
            />

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteConfirmDialogOpen}
                onClose={handleCancelDelete}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    color: isProfessionalLightDialog ? '#ffffff' : 'error.main',
                    fontWeight: 600,
                }}>
                    ⚠️ Confirm Delete
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body1" sx={{ mt: 2, mb: 2 }}>
                        <strong>Delete operation can't be recovered.</strong>
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Are you sure you want to delete this template?
                    </Typography>
                    {templateToDelete && (
                        <Box sx={{ 
                            mt: 2, 
                            p: 2, 
                            backgroundColor: 'action.hover', 
                            borderRadius: 1,
                            border: '1px solid',
                            borderColor: 'divider'
                        }}>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                Template: {getDisplayTemplateName(templateToDelete?.templateName)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                Type: {templateToDelete?.templateType?.toUpperCase()}
                            </Typography>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button 
                        onClick={handleCancelDelete} 
                        variant="outlined" 
                        color="inherit"
                        disabled={deleteLoading}
                    >
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleConfirmDelete} 
                        variant="contained" 
                        color="error"
                        disabled={deleteLoading}
                        startIcon={deleteLoading ? <CircularProgress size={16} color="inherit" /> : null}
                    >
                        {deleteLoading ? 'Deleting...' : 'Confirm Delete'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Browse System Templates Dialog */}
            <Dialog
                open={browseSystemTemplatesOpen}
                onClose={() => setBrowseSystemTemplatesOpen(false)}
                maxWidth="lg"
                fullWidth
                TransitionProps={{ onEntered: () => { setSysSearchQuery(''); setSysCategoryFilter(''); setSysSortField('lastUpdated'); setSysSortOrder('desc'); setSysVisibleCount(SYS_PAGE_SIZE); } }}
            >
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                        <Typography variant="subtitle1" component="span" sx={{ fontWeight: 700, display: 'block' }}>
                            Pre-Approved AI Generated Templates
                        </Typography>
                        <Typography variant="caption" component="span" color="text.secondary" sx={{ display: 'block' }}>
                            Click on any template to preview and create from it.
                        </Typography>
                    </Box>
                    <IconButton
                        size="small"
                        onClick={() => setBrowseSystemTemplatesOpen(false)}
                        sx={{ color: 'error.main', border: '1px solid', borderColor: 'error.main', borderRadius: 1, px: 1, py: 0.5, fontSize: '0.85rem', fontWeight: 700 }}
                    >
                        ✕
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ p: 2 }}>
                    <Box display="flex" alignItems="center" gap={1} sx={{
                        mt: 1, mb: 2, p: 1.5, borderRadius: 2,
                        backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.45)' : 'rgba(0,0,0,0.06)',
                    }} flexWrap="wrap">
                        <TextField
                            size="small"
                            placeholder="Search..."
                            value={sysSearchQuery}
                            onChange={(e) => setSysSearchQuery(e.target.value)}
                            sx={{ flexGrow: 1, maxWidth: { xs: 'none', sm: 240 } }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Search fontSize="small" color="action" />
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <FormControl size="small" sx={{ minWidth: 140 }}>
                            <Select value={sysCategoryFilter} onChange={(e) => setSysCategoryFilter(e.target.value)} displayEmpty sx={{ fontSize: '0.82rem' }}>
                                <MenuItem value="">Category: All</MenuItem>
                                {activeTab === 'whatsappbusiness' ? [
                                    <MenuItem key="MARKETING" value="MARKETING">Category: Marketing</MenuItem>,
                                    <MenuItem key="UTILITY" value="UTILITY">Category: Utility</MenuItem>,
                                    <MenuItem key="AUTHENTICATION" value="AUTHENTICATION">Category: Authentication</MenuItem>,
                                ] : [
                                    <MenuItem key="transactional" value="transactional">Category: Transactional</MenuItem>,
                                    <MenuItem key="promotional" value="promotional">Category: Promotional</MenuItem>,
                                    <MenuItem key="service" value="service">Category: Service</MenuItem>,
                                ]}
                            </Select>
                        </FormControl>
                        <Box sx={{ flex: 1 }} />
                        <FormControl size="small" sx={{ minWidth: 130 }}>
                            <Select value={sysSortField} onChange={(e) => setSysSortField(e.target.value as 'name' | 'lastUpdated')} sx={{ fontSize: '0.82rem' }}>
                                <MenuItem value="name">Sort by: Name</MenuItem>
                                <MenuItem value="lastUpdated">Sort by: Last Updated</MenuItem>
                            </Select>
                        </FormControl>
                        <FormControl size="small" sx={{ minWidth: 80 }}>
                            <Select value={sysSortOrder} onChange={(e) => setSysSortOrder(e.target.value as 'asc' | 'desc')} sx={{ fontSize: '0.82rem' }}>
                                <MenuItem value="asc">ASC</MenuItem>
                                <MenuItem value="desc">DESC</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>
                    {filteredSystemTemplates.length === 0 ? (
                        <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                            No system templates found.
                        </Typography>
                    ) : (
                        <>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: { xs: 'center', md: 'flex-start' } }}>
                                {filteredSystemTemplates.slice(0, sysVisibleCount).map((sTmpl: any) => {
                                    const tabConf = getTabConfig(sTmpl.templateType);
                                    return (
                                        <Box
                                            key={sTmpl._id}
                                            onClick={() => setSystemTemplatePreview(sTmpl)}
                                            sx={{
                                                width: 220,
                                                height: 170,
                                                borderRadius: 2.5,
                                                overflow: 'hidden',
                                                position: 'relative',
                                                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                                                cursor: 'pointer',
                                                flexShrink: 0,
                                                border: '2px solid transparent',
                                                transition: 'border-color 0.2s, box-shadow 0.2s',
                                                '&:hover': { borderColor: 'secondary.main', boxShadow: '0 4px 16px rgba(0,0,0,0.25)' },
                                            }}
                                        >
                                            {sTmpl.thumbnailUrl ? (
                                                <img
                                                    src={sTmpl.thumbnailUrl}
                                                    alt=""
                                                    style={{
                                                        position: 'absolute', top: 0, left: 0,
                                                        width: '100%', height: '100%',
                                                        objectFit: 'cover', objectPosition: 'top',
                                                    }}
                                                />
                                            ) : (
                                                <Box sx={{
                                                    position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                                                    background: tabConf.gradient,
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 0.18,
                                                }}>
                                                    {React.cloneElement(tabConf.icon, { sx: { fontSize: 56, color: '#fff' } })}
                                                </Box>
                                            )}
                                            <Box sx={{
                                                position: 'absolute', bottom: 0, left: 0, right: 0,
                                                background: 'linear-gradient(to top, rgba(0,0,0,0.88) 0%, rgba(0,0,0,0.55) 60%, transparent 100%)',
                                                px: 1.5, py: 0.75,
                                            }}>
                                                <Typography variant="subtitle2" noWrap sx={{
                                                    color: '#fff', fontWeight: 700, fontSize: '0.8rem',
                                                    textShadow: '0 1px 3px rgba(0,0,0,0.4)',
                                                }}>
                                                    {getSystemTemplateDisplayName(sTmpl.templateName)}
                                                </Typography>
                                                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.65rem' }}>
                                                    Click to preview
                                                </Typography>
                                            </Box>
                                        </Box>
                                    );
                                })}
                            </Box>
                            {sysVisibleCount < filteredSystemTemplates.length && (
                                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                                    <Button
                                        variant="outlined"
                                        color="secondary"
                                        size="small"
                                        onClick={() => setSysVisibleCount(prev => prev + SYS_PAGE_SIZE)}
                                    >
                                        Load More ({filteredSystemTemplates.length - sysVisibleCount} remaining)
                                    </Button>
                                </Box>
                            )}
                        </>
                    )}
                </DialogContent>
            </Dialog>

            {/* System Template Preview Dialog */}
            <Dialog
                open={!!systemTemplatePreview}
                onClose={() => setSystemTemplatePreview(null)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                        <Box component="span" sx={{ fontWeight: 800, fontSize: '1.25rem', display: 'block', lineHeight: 1.3 }}>
                            {systemTemplatePreview ? getSystemTemplateDisplayName(systemTemplatePreview.templateName) : ''}
                        </Box>
                        <Typography
                            variant="caption"
                            component="span"
                            sx={{
                                display: 'block',
                                color: isProfessionalLightDialog ? 'rgba(255, 255, 255, 0.85)' : 'text.secondary',
                            }}
                        >
                            System Template — Read Only
                        </Typography>
                    </Box>
                </DialogTitle>
                <DialogContent sx={{ p: 1.5, display: 'flex', flexDirection: 'column', alignItems: 'center', backgroundColor: 'background.default' }}>
                    {systemTemplatePreview?.thumbnailUrl && (
                        <img
                            src={systemTemplatePreview.thumbnailUrl}
                            alt="System template preview"
                            style={{ maxWidth: '100%', maxHeight: '60vh', borderRadius: 4, marginBottom: 12 }}
                        />
                    )}
                    {systemTemplatePreview?.templateType !== 'mail' && systemTemplatePreview?.templateContent && (
                        <Box sx={{
                            width: '100%', maxHeight: 200, overflow: 'auto',
                            p: 2, borderRadius: 1,
                            backgroundColor: 'background.paper',
                            border: '1px solid', borderColor: 'divider',
                            whiteSpace: 'pre-wrap', fontSize: '0.85rem', lineHeight: 1.6,
                        }}>
                            {systemTemplatePreview.templateContent}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 2.5, pb: 2 }}>
                    <Button
                        variant="outlined"
                        color="secondary"
                        onClick={() => setSystemTemplatePreview(null)}
                    >
                        Close
                    </Button>
                    <Button
                        variant="contained"
                        color="success"
                        onClick={() => handleCreateFromSystemTemplate(systemTemplatePreview)}
                    >
                        Create Template
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Thumbnail Preview Dialog */}
            <Dialog
                open={!!thumbnailPreview}
                onClose={() => setThumbnailPreview(null)}
                maxWidth="md"
            >
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    Template Preview
                    <Button
                        variant="outlined"
                        {...(isProfessionalLightDialog ? {} : { color: 'secondary' as const })}
                        size="small"
                        onClick={() => setThumbnailPreview(null)}
                        sx={dialogTitleCloseButtonSx}
                    >
                        Close
                    </Button>
                </DialogTitle>
                <DialogContent sx={{ p: 1, display: 'flex', justifyContent: 'center', backgroundColor: 'background.default' }}>
                    {thumbnailPreview && (
                        <img
                            src={thumbnailPreview}
                            alt="Template preview"
                            style={{ maxWidth: '100%', maxHeight: '80vh', borderRadius: 4 }}
                        />
                    )}
                </DialogContent>
            </Dialog>

        </Box>
    )
}
